/* eslint-disable */
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import CloseButton from "./CloseButton";
import { Mail, AlertTriangle, X, CheckCircle, Menu, MapPin, CreditCard, BookOpen, User, GraduationCap, Lock } from "lucide-react";

import YogaIcon from "./YogaIcon";
// 12,800 lines of dashboard across these four. None of it is needed to paint
// the landing page, and a logged-out visitor was downloading all of it before
// the hero could render — the single biggest thing in front of LCP on mobile.
// Split out, they load when a screen that actually uses them is shown.
const SchoolDashboard  = lazy(() => import('./SchoolDashboard'));
const TeacherDashboard = lazy(() => import('./TeacherDashboard'));
const StudentBrowse    = lazy(() => import('./StudentBrowse'));
import { slugFromPath, resolveSlug, nestedSlugFromPath, resolveNestedSlug } from './profileSlug';
import VerifiedBadge from './VerifiedBadge';
const AdminDashboard   = lazy(() => import('./AdminDashboard'));
const ClaimProfile     = lazy(() => import('./ClaimProfile'));
import SearchBar from './SearchBar';
import AccountSwitcher from './AccountSwitcher';
import { sendRoleWelcome } from './emailService';
import { track, EVENTS } from './analytics';
import { INDEPENDENT_TEACHERS } from './config/features';
import { SCOPE_OPTIONS } from './content/scopes';
import { fmtIn, toUsdLive, useFxRates, useVisitorCurrency } from './money';
import { summarizeProgrammes, durationLabel } from './content/programmeSummary';
import { TRUST_ITEMS, SCHOOL_PROMISE, SCHOOL_PITCH, ENQUIRY_FLOW_FULL, HOME_FAQ, ratingDisplay, NEW_LABEL, destinationCountLabel, countByDestination } from './content/policies';
import { liveRetreatFilter } from './retreatAvailability';
import { isProtectedPath, safeNext } from './appRoutes';

// Derive every role a single login holds from the entity rows it owns, so a
// school owner who also teaches can switch dashboards without a second account.
// When a Supabase access token stops working, in ms since epoch (0 if unreadable).
function tokenExpiry(token) {
  try { return JSON.parse(atob(String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp * 1000; }
  catch { return 0; }
}

async function detectRoles(userId, token, baseRole) {
  const roles = new Set();
  if (baseRole) roles.add(baseRole);
  const auth = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token || SUPABASE_ANON_KEY}` };
  try {
    const [schoolRes, teacherRes] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/schools?user_id=eq.${userId}&select=id&limit=1`, { headers: auth }),
      fetch(`${SUPABASE_URL}/rest/v1/teachers?user_id=eq.${userId}&select=id&limit=1`, { headers: auth }),
    ]);
    const schools = await schoolRes.json();
    const teachers = await teacherRes.json();
    if (Array.isArray(schools) && schools.length) roles.add("school");
    if (Array.isArray(teachers) && teachers.length) roles.add("teacher");
  } catch { /* non-blocking — fall back to base role only */ }
  return [...roles];
}

const SUPABASE_URL = "https://nqwclyzqrywyvnemrjty.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5xd2NseXpxcnl3eXZuZW1yanR5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMxNTg5MzAsImV4cCI6MjA4ODczNDkzMH0.C7uyVGVDUSiMWTGTdC_UTvJv93oRdGK4RS4LQyc6n1A";

// ── Signup intent ─────────────────────────────────────────────────────────────
// mailer_autoconfirm is off, so signUp returns no session: there is no token at
// that moment, so the role row cannot be written yet. Whatever the user typed
// has to survive until they click the confirmation link, which may be minutes
// later and on a different device.
//
// Two carriers, on purpose. user_metadata rides on the account itself, so a
// link opened on a phone after signing up on a laptop still finds it.
// localStorage is the same-browser fast path, and is what the teacher flow
// already used.
// ── Auth errors ───────────────────────────────────────────────────────────────
// GoTrue does not answer in one shape. A rejected signup comes back as
// { code, error_code, msg } with NO `error` key, so `if (res.error)` never
// fired and the request fell through to "Signup failed." — which tells the
// person nothing and, when the cause is a temporary rate limit, actively
// misleads them into thinking their details were wrong.
//
// Returns a message to show, or null when the response is fine.
function authErrorMessage(res) {
  if (!res) return "Something went wrong. Please try again.";
  const code = res.error_code || res.error || null;
  const raw  = res.error_description || res.msg || res.error?.message || null;
  if (!code && !raw) return null;

  switch (code) {
    case "over_email_send_rate_limit":
    case "over_request_rate_limit":
      // Never show the backend's wording here. It reads as a fault in what the
      // person typed, when nothing they do will help.
      return "We can't send the confirmation email right now. Please wait a few minutes and try again.";
    case "user_already_exists":
    case "email_exists":
      return "An account with this email already exists. Please sign in instead.";
    case "weak_password":
      return "Please choose a longer password — at least 6 characters.";
    case "email_address_invalid":
    case "validation_failed":
      return "Please check the email address and try again.";
    case "signup_disabled":
      return "New signups are paused right now. Please try again later.";
    case "invalid_credentials":
      return "Invalid email or password.";
    case "email_not_confirmed":
      return "Please confirm your email first — check your inbox.";
    default:
      break;
  }
  if (/already registered|already exists/i.test(raw || "")) {
    return "An account with this email already exists. Please sign in instead.";
  }
  if (res.code === 429) {
    return "Too many attempts just now. Please wait a few minutes and try again.";
  }
  console.warn("[auth] unmapped error:", res);
  return "Something went wrong. Please try again.";
}

const SIGNUP_INTENT_KEY = "ys_signup_intent";
const SIGNUP_ROLES = ["student", "teacher", "school"];

// The role decides which table gets a row, so it is never taken on trust from
// storage the user controls. The database already refuses a self-assigned
// 'admin' on insert; this keeps anything unexpected from reaching it at all.
const safeRole = (r) => (SIGNUP_ROLES.includes(r) ? r : null);

// Returns the object to hand signUp() as `data` — i.e. user_metadata.
function rememberSignupIntent(intent) {
  try { localStorage.setItem(SIGNUP_INTENT_KEY, JSON.stringify(intent)); } catch {}
  return { ys_signup: intent };
}

function readSignupIntent(userMetadata, authEmail) {
  // The account's own copy wins. It cannot belong to anyone else, and it is
  // there however the confirmation link was opened.
  const onAccount = userMetadata?.ys_signup;
  if (safeRole(onAccount?.role)) return onAccount;

  // Fall back to this browser — but only if it is for THIS account. A signup
  // that was abandoned leaves its intent behind, and without this check the
  // next person to sign in on the same browser inherits it.
  let local = null;
  try {
    const raw = localStorage.getItem(SIGNUP_INTENT_KEY);
    if (raw) local = JSON.parse(raw);
  } catch {}
  if (!safeRole(local?.role)) return null;
  if (local.email && authEmail && local.email.trim().toLowerCase() !== String(authEmail).trim().toLowerCase()) return null;
  return local;
}

function clearSignupIntent() {
  try { localStorage.removeItem(SIGNUP_INTENT_KEY); } catch {}
}

const supabase = {
  auth: {
    signUp: async ({ email, password, data }) => {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify(data ? { email, password, data } : { email, password }),
      });
      return res.json();
    },
    signIn: async ({ email, password }) => {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ email, password }),
      });
      return res.json();
    },
    signInWithGoogle: async () => {
      const redirectTo = window.location.hostname === "localhost"
        ? "http://localhost:3000"
        : "https://yogastay.app";
      window.location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=google&redirect_to=${redirectTo}`;
    },
    signOut: async (token) => {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      });
    },
  },
  from: (table) => ({
    insert: async (data, token) => {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}`, Prefer: "return=representation" },
        body: JSON.stringify(data),
      });
      return res.json();
    },
    select: async (query = "*", token, filters = "") => {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/${table}?select=${query}${filters}`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${token}` },
      });
      return res.json();
    },
  }),
};

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300&family=Jost:wght@300;400;500;600&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      /* Terracotta (primary) + Sage (secondary) — warm, grounded yoga palette */
      --primary: #B5703F; --primary-dark: #985A30; --primary-darker: #7E4A28;
      --sage: #7C8B6F; --sage-dark: #5F6E54; --sage-soft: #EDF0E8;
      --sand: #E8DCCB; --sky: #9EC6F3; --teal: #A9B89B; --teal-dark: #7C8B6F;
      --cream: #FAF7F1; --cream-dark: #F0EDE7;
      --text-dark: #2E2A24; --text-mid: #4a4a5a; --text-light: #6b6b7a;
      --error: #C0392B; --border: rgba(181,112,63,0.25);
      --shadow-sm: 0 1px 4px rgba(0,0,0,0.07);
      --shadow-md: 0 4px 16px rgba(0,0,0,0.10);
      --shadow-lg: 0 12px 40px rgba(0,0,0,0.14);
      --radius-sm: 6px; --radius-md: 10px; --radius-lg: 16px; --radius-xl: 24px;
    }
    body { font-family: 'Jost', sans-serif; background: #FAF7F1; color: #2E2A24; min-height: 100vh; -webkit-font-smoothing: antialiased; }
    html { scroll-behavior: smooth; }
    .serif { font-family: 'Cormorant Garamond', serif; }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
    @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

    input, textarea, select {
      font-family: 'Jost', sans-serif;
      font-size: 16px !important;
      color: #2E2A24;
      outline: none;
      transition: border-color 0.15s;
      -webkit-appearance: none;
    }
    /* The reset above is for text fields. On iOS Safari a checkbox or radio
       with appearance:none draws nothing at all — no box, no tick, nothing to
       aim at — which made the claim page's consent box impossible to tick. */
    input[type="checkbox"], input[type="radio"] {
      -webkit-appearance: auto; appearance: auto;
      width: 18px; height: 18px; accent-color: #B5703F; cursor: pointer; flex-shrink: 0;
    }
    input:focus, textarea:focus, select:focus { border-color: #B5703F !important; box-shadow: 0 0 0 3px rgba(181,112,63,0.15); }
    input::placeholder, textarea::placeholder { color: #6b6b7a; }

    .btn-primary { width: 100%; padding: 14px; background: #B5703F; color: white; border: none; font-family: 'Jost', sans-serif; font-size: 12px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; border-radius: 6px; display: flex; align-items: center; justify-content: center; gap: 8px; transition: background 0.15s; min-height: 48px; }
    .btn-primary:hover { background: #985A30; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    .btn-ghost { background: none; border: 1.5px solid rgba(181,112,63,0.4); padding: 12px 24px; font-family: 'Jost', sans-serif; font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; color: #4a4a5a; border-radius: 6px; transition: all 0.15s; min-height: 48px; }
    .btn-ghost:hover { border-color: #B5703F; color: #985A30; background: rgba(181,112,63,0.08); }
    .btn-google { width: 100%; padding: 13px; background: white; color: #2E2A24; border: 1.5px solid rgba(181,112,63,0.3); font-family: 'Jost', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer; border-radius: 6px; display: flex; align-items: center; justify-content: center; gap: 10px; transition: all 0.15s; min-height: 48px; }
    .btn-google:hover { border-color: #B5703F; background: #f8f9ff; }
    .spinner { width: 16px; height: 16px; border: 1.5px solid rgba(255,255,255,0.4); border-top-color: white; border-radius: 50%; animation: spin 0.7s linear infinite; display: inline-block; }
    ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: transparent; } ::-webkit-scrollbar-thumb { background: rgba(181,112,63,0.3); border-radius: 2px; }

    /* FAQ accordion */
    .faq-item summary::-webkit-details-marker { display: none; }
    .faq-item .faq-plus { transition: transform 0.2s ease; }
    .faq-item[open] .faq-plus { transform: rotate(45deg); }
    .faq-item[open] { box-shadow: 0 4px 18px rgba(181,112,63,0.1); }

    /* ── MOBILE NAVIGATION ───────────────────────────── */
    .mobile-nav-btn {
      display: none;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      color: #2E2A24;
      font-size: 24px;
    }
    .mobile-menu {
      display: none;
      position: fixed;
      inset: 0;
      background: white;
      z-index: 500;
      flex-direction: column;
      padding: 24px 20px;
      animation: slideUp 0.3s ease;
    }
    .mobile-menu.open { display: flex; }

    /* ── RESPONSIVE BREAKPOINTS ──────────────────────── */

    /* Tablet */
    @media (max-width: 900px) {
      .hide-tablet { display: none !important; }
      .auth-left-panel { width: 35% !important; padding: 32px 28px !important; }
      .auth-left-panel h2 { font-size: 28px !important; }
    }

    /* Mobile */
    @media (max-width: 768px) {
      .hide-mobile { display: none !important; }
      .mobile-nav-btn { display: flex !important; align-items: center; justify-content: center; }
      .auth-left-panel { display: none !important; }
      .auth-right-panel { width: 100% !important; padding: 24px 20px !important; max-height: unset !important; }

      /* Landing nav */
      .landing-nav { padding: 0 16px !important; height: 56px !important; }
      .landing-nav-logo span { font-size: 18px !important; }

      /* Hero */
      .hero-container { height: auto !important; min-height: 520px; padding: 40px 16px !important; }
      .hero-title { font-size: 32px !important; }
      .hero-subtitle { font-size: 14px !important; margin-bottom: 24px !important; }

      /* Search box — stack vertically */
      .search-box { flex-direction: column !important; border-radius: 12px !important; overflow: hidden; }
      .search-box-field { border-right: none !important; border-bottom: 1px solid rgba(181,112,63,0.15) !important; min-width: unset !important; width: 100% !important; padding: 12px 16px !important; }
      .search-box-btn { width: 100% !important; border-radius: 0 !important; padding: 14px !important; }

      /* Popular pills */
      .popular-pills { justify-content: flex-start !important; overflow-x: auto; padding-bottom: 4px; flex-wrap: nowrap !important; }
      .popular-pills::-webkit-scrollbar { display: none; }

      /* Trust bar */
      .trust-bar { gap: 20px !important; padding: 16px !important; flex-wrap: wrap; }
      .trust-bar > div { flex: 1 1 40%; }

      /* Section padding */
      .main-content { padding: 32px 16px !important; }
      .section-mb { margin-bottom: 40px !important; }

      /* Cards - single column */
      .featured-grid { grid-template-columns: 1fr !important; gap: 16px !important; }
      .duration-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
      .destination-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
      .testimonials-grid { grid-template-columns: 1fr !important; }
      .styles-grid { grid-template-columns: repeat(3, 1fr) !important; gap: 8px !important; }
      .category-grid { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }

      /* How it works + For Schools band */
      .how-grid { gap: 12px !important; }
      .for-schools { grid-template-columns: 1fr !important; gap: 28px !important; padding: 32px 22px !important; }

      /* CTA */
      .cta-section { padding: 32px 20px !important; }
      .cta-buttons { flex-direction: column !important; }
      .cta-buttons button { width: 100% !important; }

      /* Footer */
      .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 24px !important; }
      .footer-newsletter { flex-direction: column !important; gap: 12px !important; }
      .footer-newsletter > div:last-child { flex-direction: column !important; width: 100% !important; }
      .footer-newsletter input { width: 100% !important; }
      .footer-newsletter button { width: 100% !important; }
      .footer-bottom { flex-direction: column !important; gap: 12px !important; text-align: center; }

      /* Auth forms */
      .school-signup-steps { padding: 24px 20px !important; }
    }

    /* Small mobile */
    @media (max-width: 480px) {
      .hero-title { font-size: 26px !important; }
      .hero-br { display: none; }   /* let the headline wrap naturally, never clip */
      .duration-grid { grid-template-columns: 1fr !important; }
      .destination-grid { grid-template-columns: 1fr 1fr !important; }
      .styles-grid { grid-template-columns: repeat(2, 1fr) !important; }
      .featured-card-img { height: 160px !important; }
      .footer-grid { grid-template-columns: 1fr !important; }
      .trust-bar > div { flex: 1 1 100%; }
    }

    /* Touch targets */
    @media (max-width: 768px) {
      button { min-height: 44px; }
      a { min-height: 44px; display: inline-flex; align-items: center; }
    }

    /* Prevent iOS input zoom */
    @media (max-width: 768px) {
      input[type="text"],
      input[type="email"],
      input[type="password"],
      input[type="tel"],
      input[type="number"],
      input[type="url"],
      select,
      textarea { font-size: 16px !important; }
    }
  `}</style>
);

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"/>
      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"/>
      <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"/>
      <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.31z"/>
    </svg>
  );
}

function Divider() {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, margin:"20px 0" }}>
      <div style={{ flex:1, height:1, background:"rgba(181,112,63,0.2)" }} />
      <span style={{ fontSize:12, color:"#6b6b7a", letterSpacing:1 }}>OR</span>
      <div style={{ flex:1, height:1, background:"rgba(181,112,63,0.2)" }} />
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ marginBottom:20 }}>
      <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:8, fontWeight:500 }}>{label}</label>
      {children}
      {error && <p style={{ color:"#C0392B", fontSize:12, marginTop:6, display:"flex", alignItems:"center", gap:5 }}><AlertTriangle size={13} style={{flexShrink:0}}/>{error}</p>}
    </div>
  );
}

function LeftPanel({ mode, onLogoClick }) {
  return (
    <div className="auth-left-panel" style={{ width:"40%", minHeight:"100vh", background:mode==="school"?"linear-gradient(160deg,#985A30,#B5703F)":"linear-gradient(160deg,#7C8B6F,#A9B89B)", display:"flex", flexDirection:"column", justifyContent:"space-between", padding:"48px 52px" }}>
      <div>
        <div onClick={onLogoClick} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:80, cursor:"pointer", width:"fit-content" }}>
          <div style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,255,255,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}><YogaIcon size={20} color="white"/></div>
          <span className="serif" style={{ color:"white", fontSize:22, fontWeight:300 }}>YogaStay</span>
        </div>
        <p style={{ color:"rgba(255,255,255,0.7)", fontSize:10, letterSpacing:4, textTransform:"uppercase", marginBottom:16 }}>{mode==="school"?"For Schools":"For Students"}</p>
        <h2 className="serif" style={{ color:"white", fontSize:40, fontWeight:300, lineHeight:1.2, marginBottom:20 }}>
          {mode==="school"?"Share your sacred space":"Begin your inner journey"}
        </h2>
        <p style={{ color:"rgba(255,255,255,0.65)", lineHeight:1.9, fontSize:14, maxWidth:280 }}>
          {mode==="school"?"List classes, manage bookings, and connect with students seeking transformation.":"Discover authentic yoga schools and book sessions that resonate with your path."}
        </p>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        {(mode==="school"
          ? [`List your classes and retreats. ${SCHOOL_PROMISE}`,"Manage availability, bookings and student messages in one place","Get discovered by students across India, Bali, Thailand and beyond"]
          : ["Browse yoga schools — every listing is manually reviewed","Send a free enquiry in minutes — the school confirms and you pay the school directly","Cancellation and refunds follow each school's own terms"]
        ).map((line,i) => (
          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
            <span style={{ color:"rgba(255,255,255,0.7)", fontSize:16, lineHeight:1, marginTop:1 }}>·</span>
            <span style={{ color:"rgba(255,255,255,0.75)", fontSize:13, lineHeight:1.6 }}>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailConfirmScreen({ email, onSwitch, onBack, mode }) {
  return (
    <div style={{ padding:"48px 28px", background:"#FFF1D5", minHeight:"100vh", display:"flex", flexDirection:"column", justifyContent:"center" }}>
      <div style={{ maxWidth:400, margin:"0 auto", textAlign:"center", width:"100%" }}>
        <div style={{ width:80, height:80, borderRadius:"50%", background:"rgba(181,112,63,0.15)", border:"2px solid rgba(181,112,63,0.35)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 28px" }}><Mail size={36} color="#B5703F"/></div>
        <h2 className="serif" style={{ fontSize:28, fontWeight:300, color:"#2E2A24", marginBottom:12 }}>Check your email</h2>
        <p style={{ color:"#6b6b7a", fontSize:14, lineHeight:1.8, marginBottom:6 }}>We sent a confirmation link to</p>
        <p style={{ color:"#B5703F", fontWeight:600, fontSize:15, marginBottom:24 }}>{email}</p>
        <p style={{ color:"#6b6b7a", fontSize:13, lineHeight:1.8, marginBottom:28 }}>Click the link in your email to activate your account. Check spam if you don't see it.</p>
        <button onClick={() => onSwitch("login")} style={{ width:"100%", padding:"14px", background:"#B5703F", border:"none", borderRadius:6, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"white", marginBottom:12, minHeight:48 }}>Go to Sign In →</button>
        <button onClick={onBack} style={{ background:"none", border:"none", cursor:"pointer", color:"#6b6b7a", fontFamily:"'Jost',sans-serif", fontSize:13, minHeight:44 }}>← Back</button>
      </div>
    </div>
  );
}

function GoogleRoleSelect({ googleUser, onSuccess }) {
  const [role, setRole] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [schoolForm, setSchoolForm] = useState({ schoolName:"", location:"", description:"", style:[] });
  const styles = ["Hatha","Vinyasa","Yin","Kundalini","Meditation","Ashtanga","Restorative","Power"];
  const inputStyle = { width:"100%", padding:"12px 16px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:6, fontSize:16, background:"white", fontFamily:"'Jost',sans-serif", outline:"none" };

  const handleComplete = async () => {
    setLoading(true); setError("");
    try {
      const { token, userId, email, name } = googleUser;
      const upsertHeaders = { "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"resolution=merge-duplicates,return=representation" };

      if (role === "student") {
        const pRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?on_conflict=id`, { method:"POST", headers:upsertHeaders, body:JSON.stringify({ id:userId, role:"student", full_name:name }) });
        if (!pRes.ok) { const e = await pRes.text(); console.error("profiles insert failed:", e); throw new Error(e); }
        await fetch(`${SUPABASE_URL}/rest/v1/students?on_conflict=user_id`, { method:"POST", headers:upsertHeaders, body:JSON.stringify({ user_id:userId, full_name:name, yoga_goals:[] }) });
        await sendRoleWelcome({ role:"student", email, name, token });
        onSuccess({ token, userId, role:"student", name, email });
      } else if (role === "teacher") {
        const pRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?on_conflict=id`, { method:"POST", headers:upsertHeaders, body:JSON.stringify({ id:userId, role:"teacher", full_name:name }) });
        if (!pRes.ok) { const e = await pRes.text(); console.error("profiles insert failed:", e); throw new Error(e); }
        // Check if teacher record already exists — only create if it doesn't (prevents wiping saved profile data)
        const tCheck = await fetch(`${SUPABASE_URL}/rest/v1/teachers?user_id=eq.${userId}&select=id&limit=1`, { headers:{ apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` } });
        const tExisting = await tCheck.json();
        if (!Array.isArray(tExisting) || tExisting.length === 0) {
          await fetch(`${SUPABASE_URL}/rest/v1/teachers`, { method:"POST", headers:upsertHeaders, body:JSON.stringify({ user_id:userId, name, email, is_independent:true, verification_status:"unverified", subscription_status:"inactive", specialties:[], teaching_levels:[], languages:[], certificate_urls:[], document_urls:[], online_classes:[] }) });
        }
        await sendRoleWelcome({ role:"teacher", email, name, token });
        onSuccess({ token, userId, role:"teacher", name, email });
      } else {
        if (!schoolForm.schoolName || !schoolForm.location || !schoolForm.description || schoolForm.style.length === 0) { setError("Please fill in all school details"); setLoading(false); return; }
        const pRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?on_conflict=id`, { method:"POST", headers:upsertHeaders, body:JSON.stringify({ id:userId, role:"school", full_name:schoolForm.schoolName }) });
        if (!pRes.ok) { const e = await pRes.text(); console.error("profiles insert failed:", e); throw new Error(e); }
        await fetch(`${SUPABASE_URL}/rest/v1/schools`, { method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"return=representation" }, body:JSON.stringify({ user_id:userId, name:schoolForm.schoolName, description:schoolForm.description, location:schoolForm.location, style:schoolForm.style, contact_email:email||null, verified:false }) });
        await sendRoleWelcome({ role:"school", email, name:schoolForm.schoolName, schoolName:schoolForm.schoolName, schoolCity:schoolForm.location, token });
        onSuccess({ token, userId, role:"school", name:schoolForm.schoolName, email });
      }
    } catch(err) { setError(err?.message || "Something went wrong. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#FFF1D5", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div style={{ background:"white", borderRadius:16, width:"100%", maxWidth:480, overflow:"hidden", boxShadow:"0 8px 40px rgba(181,112,63,0.2)" }}>
        <div style={{ background:"linear-gradient(135deg,#985A30,#B5703F)", padding:"24px 24px", textAlign:"center" }}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:"rgba(255,255,255,0.25)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px" }}><YogaIcon size={26} color="white"/></div>
          <h2 className="serif" style={{ color:"white", fontSize:24, fontWeight:300, marginBottom:4 }}>Welcome to YogaStay</h2>
          <p style={{ color:"rgba(255,255,255,0.75)", fontSize:13 }}>{googleUser.email}</p>
        </div>
        <div style={{ padding:"24px 20px" }}>
          {step === 1 && (
            <>
              <h3 style={{ fontSize:17, fontWeight:500, color:"#2E2A24", marginBottom:6, textAlign:"center" }}>How will you use YogaStay?</h3>
              <p style={{ fontSize:13, color:"#6b6b7a", textAlign:"center", marginBottom:20 }}>Choose your account type</p>
              <div style={{ display:"grid", gridTemplateColumns: INDEPENDENT_TEACHERS ? "1fr 1fr 1fr" : "1fr 1fr", gap:10, marginBottom:20 }}>
                {[{r:"student",icon:<User size={28} color="#B5703F"/>,title:"I'm a Student",desc:"Browse & book retreats"},...(INDEPENDENT_TEACHERS ? [{r:"teacher",icon:<GraduationCap size={28} color="#B5703F"/>,title:"I'm a Yoga Guru",desc:"List your own classes"}] : []),{r:"school",icon:<BookOpen size={28} color="#B5703F"/>,title:"I'm a School",desc:"List classes & bookings"}].map(({ r, icon, title, desc }) => (
                  <div key={r} onClick={() => setRole(r)} style={{ padding:"18px 14px", border:"2px solid", borderRadius:10, cursor:"pointer", textAlign:"center", transition:"all 0.2s", borderColor:role===r?"#B5703F":"rgba(181,112,63,0.25)", background:role===r?"rgba(181,112,63,0.08)":"white" }}>
                    <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>{icon}</div>
                    <div style={{ fontWeight:600, fontSize:13, color:"#2E2A24", marginBottom:4 }}>{title}</div>
                    <div style={{ fontSize:11, color:"#6b6b7a" }}>{desc}</div>
                    {role === r && <div style={{ marginTop:8, fontSize:11, color:"#B5703F", fontWeight:500 }}>Selected</div>}
                  </div>
                ))}
              </div>
              <button onClick={() => { if (role==="student") handleComplete(); else if (role==="teacher") handleComplete(); else if (role==="school") setStep(2); }} disabled={!role||loading}
                style={{ width:"100%", padding:"14px", background:role?"#B5703F":"rgba(181,112,63,0.3)", border:"none", borderRadius:6, cursor:role?"pointer":"not-allowed", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"white", fontWeight:500, minHeight:48 }}>
                {loading?"Setting up...":role==="student"?"Start Browsing →":role==="teacher"?"Continue as Teacher →":role==="school"?"Continue →":"Select an option"}
              </button>
            </>
          )}
          {step === 2 && (
            <>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
                <button onClick={() => setStep(1)} style={{ background:"none", border:"none", cursor:"pointer", color:"#6b6b7a", fontSize:20, minHeight:44 }}>←</button>
                <div>
                  <h3 style={{ fontSize:17, fontWeight:500, color:"#2E2A24", marginBottom:2 }}>About Your School</h3>
                  <p style={{ fontSize:12, color:"#6b6b7a" }}>Tell students about your yoga school</p>
                </div>
              </div>
              {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"10px 14px", marginBottom:14, color:"#C0392B", fontSize:13, display:"flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} style={{flexShrink:0}}/>{error}</div>}
              <div style={{ marginBottom:12 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:7 }}>School Name *</label>
                <input value={schoolForm.schoolName} onChange={e => setSchoolForm(f=>({...f,schoolName:e.target.value}))} placeholder="e.g. Ananda Yoga Studio" style={{ ...inputStyle, marginBottom:0 }} />
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:7 }}>Location *</label>
                <input value={schoolForm.location} onChange={e => setSchoolForm(f=>({...f,location:e.target.value}))} placeholder="e.g. Rishikesh, India" style={inputStyle} />
              </div>
              <div style={{ marginBottom:12 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:7 }}>Description *</label>
                <textarea value={schoolForm.description} onChange={e => setSchoolForm(f=>({...f,description:e.target.value}))} rows={3} placeholder="Describe your school..." style={{ ...inputStyle, resize:"vertical" }} />
              </div>
              <div style={{ marginBottom:20 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:10 }}>Yoga Styles *</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {["Hatha","Vinyasa","Yin","Kundalini","Meditation","Ashtanga","Restorative","Power"].map(s => (
                    <button key={s} onClick={() => setSchoolForm(f=>({...f,style:f.style.includes(s)?f.style.filter(x=>x!==s):[...f.style,s]}))} style={{ padding:"8px 14px", border:"1.5px solid", borderRadius:20, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, transition:"all 0.15s", borderColor:schoolForm.style.includes(s)?"#B5703F":"rgba(181,112,63,0.3)", background:schoolForm.style.includes(s)?"#B5703F":"transparent", color:schoolForm.style.includes(s)?"white":"#4a4a5a", minHeight:40 }}>{s}</button>
                  ))}
                </div>
              </div>
              <button onClick={handleComplete} disabled={loading} style={{ width:"100%", padding:"14px", background:"#B5703F", border:"none", borderRadius:6, cursor:loading?"not-allowed":"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:2, textTransform:"uppercase", color:"white", fontWeight:500, opacity:loading?0.7:1, minHeight:48 }}>
                {loading?"Registering...":"Register School →"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SchoolSignUp({ onSwitch, onSuccess }) {
  useEffect(() => { track(EVENTS.SCHOOL_SIGNUP_STARTED); }, []);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [form, setForm] = useState({ email:"", password:"", confirmPassword:"", schoolName:"", location:"", address:"", description:"", style:[] });
  const [fieldErrors, setFieldErrors] = useState({});
  const styles = ["Hatha","Vinyasa","Yin","Kundalini","Meditation","Ashtanga","Restorative","Power"];
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleStyle = (s) => set("style", form.style.includes(s)?form.style.filter(x=>x!==s):[...form.style,s]);
  const inputStyle = { width:"100%", padding:"12px 16px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:6, fontSize:16, background:"white", fontFamily:"'Jost',sans-serif", outline:"none" };

  const validateStep1 = () => {
    const e = {};
    if (!form.email) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.password || form.password.length < 6) e.password = "Minimum 6 characters";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords do not match";
    setFieldErrors(e); return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!form.schoolName) e.schoolName = "School name is required";
    if (!form.location) e.location = "Location is required";
    if (!form.description) e.description = "Please describe your school";
    if (form.style.length === 0) e.style = "Select at least one style";
    setFieldErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateStep2()) return;
    setLoading(true); setError("");
    try {
      // Everything from step 2 goes with the signup. Without this it is gone by
      // the time they confirm, and they are asked to type it all again.
      const intent = { role:"school", name:form.schoolName, email:form.email,
                       description:form.description, location:form.location,
                       address:form.address?.trim()||null, style:form.style };
      const authData = await supabase.auth.signUp({ email:form.email, password:form.password, data:rememberSignupIntent(intent) });
      { const m = authErrorMessage(authData); if (m) throw new Error(m); }
      // GoTrue returns the user object at the TOP LEVEL when confirmation is
      // required, and wrapped in `user` only when a session comes back. Reading
      // just authData.user.id threw "Signup failed." at people whose account had
      // in fact been created — so they never saw the check-your-email screen,
      // and a retry told them the address was already registered.
      const userId = authData.user?.id || authData.id;
      if (!userId) throw new Error("Signup failed.");
      const token = authData.access_token;
      if (token) {
        clearSignupIntent();                       // rows written here, nothing to replay
        await supabase.from("profiles").insert({ id:userId, role:"school", full_name:form.schoolName }, token);
        await supabase.from("schools").insert({ user_id:userId, name:form.schoolName, description:form.description, location:form.location, address:form.address?.trim()||null, style:form.style, contact_email:form.email?.trim()||null, verified:false }, token);
        await sendRoleWelcome({ role:"school", email:form.email, name:form.schoolName, schoolName:form.schoolName, schoolCity:form.location, token });
      }
      setConfirmEmail(form.email); setShowConfirm(true);
    } catch (err) { setError(err.message || "Something went wrong."); }
    setLoading(false);
  };

  if (showConfirm) return <EmailConfirmScreen email={confirmEmail} onSwitch={onSwitch} onBack={() => setShowConfirm(false)} mode="school" />;

  return (
    <div className="auth-right-panel school-signup-steps" style={{ padding:"32px 28px", overflowY:"auto", maxHeight:"100vh", background:"#FFF1D5" }}>
      <div style={{ marginBottom:28 }}>
        <div style={{ display:"flex", gap:6, marginBottom:20 }}>
          {[1,2].map(s => <div key={s} style={{ height:3, flex:1, borderRadius:2, background:step>=s?"#B5703F":"rgba(181,112,63,0.2)", transition:"background 0.3s" }} />)}
        </div>
        <p style={{ fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:6 }}>Step {step} of 2</p>
        <h1 className="serif" style={{ fontSize:28, fontWeight:300, color:"#2E2A24" }}>{step===1?"Create Account":"Your School"}</h1>
      </div>
      {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"12px 16px", marginBottom:16, color:"#C0392B", fontSize:13, display:"flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} style={{flexShrink:0}}/>{error}</div>}
      {step === 1 && (
        <div>
          <button className="btn-google" onClick={() => supabase.auth.signInWithGoogle()}><GoogleIcon /> Continue with Google</button>
          <Divider />
          <Field label="Email Address" error={fieldErrors.email}>
            <input type="email" placeholder="school@example.com" value={form.email} onChange={e => set("email",e.target.value)} style={{ ...inputStyle, borderColor:fieldErrors.email?"#C0392B":"rgba(181,112,63,0.3)" }} />
          </Field>
          <Field label="Password" error={fieldErrors.password}>
            <PasswordInput value={form.password} onChange={e => set("password",e.target.value)} placeholder="Minimum 6 characters" style={inputStyle} borderColor={fieldErrors.password?"#C0392B":undefined} />
          </Field>
          <Field label="Confirm Password" error={fieldErrors.confirmPassword}>
            <PasswordInput value={form.confirmPassword} onChange={e => set("confirmPassword",e.target.value)} placeholder="Repeat your password" style={inputStyle} borderColor={fieldErrors.confirmPassword?"#C0392B":undefined} />
          </Field>
          <button className="btn-primary" onClick={() => { if (validateStep1()) setStep(2); }}>Continue →</button>
        </div>
      )}
      {step === 2 && (
        <div>
          <Field label="School Name" error={fieldErrors.schoolName}>
            <input placeholder="e.g. Ananda Yoga Studio" value={form.schoolName} onChange={e => set("schoolName",e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Location" error={fieldErrors.location}>
            <input placeholder="e.g. Rishikesh, India" value={form.location} onChange={e => set("location",e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Full Address (Optional — for map pin)">
            <input placeholder="e.g. Jl. Raya Ubud No.8, Ubud, Gianyar 80571, Bali" value={form.address} onChange={e => set("address",e.target.value)} style={inputStyle} />
            <p style={{ fontSize:11, color:"#6b6b7a", marginTop:4 }}>Helps students find you on the map. You can add or update this later in your profile.</p>
          </Field>
          <Field label="Description" error={fieldErrors.description}>
            <textarea placeholder="Describe your school..." value={form.description} onChange={e => set("description",e.target.value)} rows={3} style={{ ...inputStyle, resize:"vertical" }} />
          </Field>
          <Field label="Yoga Styles" error={fieldErrors.style}>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {styles.map(s => (
                <button key={s} onClick={() => toggleStyle(s)} style={{ padding:"8px 14px", border:"1.5px solid", borderRadius:20, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, transition:"all 0.15s", borderColor:form.style.includes(s)?"#B5703F":"rgba(181,112,63,0.3)", background:form.style.includes(s)?"#B5703F":"transparent", color:form.style.includes(s)?"white":"#4a4a5a", minHeight:40 }}>{s}</button>
              ))}
            </div>
          </Field>
          <div style={{ display:"flex", gap:12 }}>
            <button className="btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={loading}>{loading?<><div className="spinner"/>Registering...</>:"Register School →"}</button>
          </div>
        </div>
      )}
      <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid rgba(181,112,63,0.15)", textAlign:"center" }}>
        <p style={{ fontSize:13, color:"#6b6b7a" }}>Already have an account?{" "}<button onClick={() => onSwitch("login")} style={{ background:"none", border:"none", cursor:"pointer", color:"#985A30", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500, minHeight:44 }}>Sign In</button></p>
        <p style={{ fontSize:13, color:"#6b6b7a", marginTop:6 }}>Are you a student?{" "}<button onClick={() => onSwitch("student-signup")} style={{ background:"none", border:"none", cursor:"pointer", color:"#7C8B6F", fontFamily:"'Jost',sans-serif", fontSize:13, minHeight:44 }}>Sign up here</button></p>
        {INDEPENDENT_TEACHERS && <p style={{ fontSize:13, color:"#6b6b7a", marginTop:6 }}>Teach yoga independently?{" "}<button onClick={() => onSwitch("teacher-signup")} style={{ background:"none", border:"none", cursor:"pointer", color:"#B5703F", fontFamily:"'Jost',sans-serif", fontSize:13, minHeight:44 }}>Join as a Yoga Guru</button></p>}
      </div>
    </div>
  );
}

function StudentSignUp({ onSwitch, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [form, setForm] = useState({ fullName:"", email:"", password:"", confirmPassword:"", goals:[] });
  const [fieldErrors, setFieldErrors] = useState({});
  const goals = ["Stress Relief","Flexibility","Strength","Mindfulness","Spirituality","Weight Loss","Injury Recovery","Better Sleep"];
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleGoal = (g) => set("goals", form.goals.includes(g)?form.goals.filter(x=>x!==g):[...form.goals,g]);
  const inputStyle = { width:"100%", padding:"12px 16px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:6, fontSize:16, background:"white", fontFamily:"'Jost',sans-serif", outline:"none" };

  const validate = () => {
    const e = {};
    if (!form.fullName) e.fullName = "Name is required";
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.password || form.password.length < 6) e.password = "Minimum 6 characters";
    if (form.password !== form.confirmPassword) e.confirmPassword = "Passwords do not match";
    setFieldErrors(e); return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError("");
    try {
      const intent = { role:"student", name:form.fullName, email:form.email, goals:form.goals };
      const authData = await supabase.auth.signUp({ email:form.email, password:form.password, data:rememberSignupIntent(intent) });
      { const m = authErrorMessage(authData); if (m) throw new Error(m); }
      // GoTrue returns the user object at the TOP LEVEL when confirmation is
      // required, and wrapped in `user` only when a session comes back. Reading
      // just authData.user.id threw "Signup failed." at people whose account had
      // in fact been created — so they never saw the check-your-email screen,
      // and a retry told them the address was already registered.
      const userId = authData.user?.id || authData.id;
      if (!userId) throw new Error("Signup failed.");
      const token = authData.access_token;
      if (token) {
        clearSignupIntent();
        await supabase.from("profiles").insert({ id:userId, role:"student", full_name:form.fullName }, token);
        await supabase.from("students").insert({ user_id:userId, full_name:form.fullName, yoga_goals:form.goals }, token);
        await sendRoleWelcome({ role:"student", email:form.email, name:form.fullName, token });
      }
      setConfirmEmail(form.email); setShowConfirm(true);
    } catch (err) { setError(err.message || "Something went wrong."); }
    setLoading(false);
  };

  if (showConfirm) return <EmailConfirmScreen email={confirmEmail} onSwitch={onSwitch} onBack={() => setShowConfirm(false)} mode="student" />;

  return (
    <div className="auth-right-panel" style={{ padding:"32px 28px", overflowY:"auto", maxHeight:"100vh", background:"#FFF1D5" }}>
      <div style={{ marginBottom:24 }}>
        <p style={{ fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:6 }}>Student Registration</p>
        <h1 className="serif" style={{ fontSize:28, fontWeight:300, color:"#2E2A24" }}>Start Your Practice</h1>
      </div>
      {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"12px 16px", marginBottom:16, color:"#C0392B", fontSize:13, display:"flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} style={{flexShrink:0}}/>{error}</div>}
      <button className="btn-google" onClick={() => supabase.auth.signInWithGoogle()}><GoogleIcon /> Continue with Google</button>
      <Divider />
      <Field label="Full Name" error={fieldErrors.fullName}>
        <input placeholder="Your full name" value={form.fullName} onChange={e => set("fullName",e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Email Address" error={fieldErrors.email}>
        <input type="email" placeholder="you@example.com" value={form.email} onChange={e => set("email",e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Password" error={fieldErrors.password}>
        <PasswordInput value={form.password} onChange={e => set("password",e.target.value)} placeholder="Minimum 6 characters" style={inputStyle} borderColor={fieldErrors.password?"#C0392B":undefined} />
      </Field>
      <Field label="Confirm Password" error={fieldErrors.confirmPassword}>
        <PasswordInput value={form.confirmPassword} onChange={e => set("confirmPassword",e.target.value)} placeholder="Repeat your password" style={inputStyle} borderColor={fieldErrors.confirmPassword?"#C0392B":undefined} />
      </Field>
      <Field label="Your Yoga Goals (optional)">
        <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
          {goals.map(g => (
            <button key={g} onClick={() => toggleGoal(g)} style={{ padding:"8px 14px", border:"1.5px solid", borderRadius:20, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, transition:"all 0.15s", borderColor:form.goals.includes(g)?"#A9B89B":"rgba(181,112,63,0.3)", background:form.goals.includes(g)?"#A9B89B":"transparent", color:form.goals.includes(g)?"#2E2A24":"#4a4a5a", minHeight:40 }}>{g}</button>
          ))}
        </div>
      </Field>
      <button className="btn-primary" onClick={handleSubmit} disabled={loading}>{loading?<><div className="spinner"/>Creating Account...</>:"Create Student Account →"}</button>
      <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid rgba(181,112,63,0.15)", textAlign:"center" }}>
        <p style={{ fontSize:13, color:"#6b6b7a" }}>Already have an account?{" "}<button onClick={() => onSwitch("login")} style={{ background:"none", border:"none", cursor:"pointer", color:"#985A30", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500, minHeight:44 }}>Sign In</button></p>
        <p style={{ fontSize:13, color:"#6b6b7a", marginTop:6 }}>Are you a yoga school?{" "}<button onClick={() => onSwitch("school-signup")} style={{ background:"none", border:"none", cursor:"pointer", color:"#7C8B6F", fontFamily:"'Jost',sans-serif", fontSize:13, minHeight:44 }}>Register here</button></p>
        {INDEPENDENT_TEACHERS && <p style={{ fontSize:13, color:"#6b6b7a", marginTop:6 }}>Teach yoga independently?{" "}<button onClick={() => onSwitch("teacher-signup")} style={{ background:"none", border:"none", cursor:"pointer", color:"#B5703F", fontFamily:"'Jost',sans-serif", fontSize:13, minHeight:44 }}>Join as a Yoga Guru</button></p>}
      </div>
    </div>
  );
}


function TeacherSignUp({ onSwitch, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({ name:"", email:"", password:"" });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inputStyle = { width:"100%", padding:"12px 16px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:6, fontSize:16, background:"white", fontFamily:"'Jost',sans-serif", outline:"none" };

  const handleSignUp = async () => {
    if (!form.name.trim())  { setError("Please enter your full name."); return; }
    if (!form.email.trim()) { setError("Please enter your email."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      const intent = { role:"teacher", name:form.name, email:form.email };
      const data = await supabase.auth.signUp({ email:form.email, password:form.password, data:rememberSignupIntent(intent) });
      { const m = authErrorMessage(data); if (m) throw new Error(m); }
      const token  = data.access_token;
      const userId = data.user?.id || data.id;
      if (!token || !userId) {
        // Email confirmation required — the intent is already stored, in the
        // browser and on the account. Replayed when they click the link.
        setShowConfirm(true); setLoading(false); return;
      }
      clearSignupIntent();
      // Token available (auto-confirm) — create rows immediately
      const pRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?on_conflict=id`, {
        method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"resolution=merge-duplicates,return=representation" },
        body:JSON.stringify({ id:userId, role:"teacher", full_name:form.name }),
      });
      if (!pRes.ok) { const e = await pRes.text(); console.error("profiles insert failed:", e); throw new Error(e); }
      // Only create teacher record if it doesn't already exist (prevents wiping saved profile)
      const tCheck2 = await fetch(`${SUPABASE_URL}/rest/v1/teachers?user_id=eq.${userId}&select=id&limit=1`, { headers:{ apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` } });
      const tExist2 = await tCheck2.json();
      if (!Array.isArray(tExist2) || tExist2.length === 0) {
        await fetch(`${SUPABASE_URL}/rest/v1/teachers`, {
          method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"return=representation" },
          body:JSON.stringify({ user_id:userId, name:form.name, email:form.email, is_independent:true, verification_status:"unverified", subscription_status:"inactive", specialties:[], teaching_levels:[], languages:[], certificate_urls:[], document_urls:[], online_classes:[] }),
        });
      }
      if (data.refresh_token) localStorage.setItem("yogastay_refresh_token", data.refresh_token);
      await sendRoleWelcome({ role:"teacher", email:form.email, name:form.name, token });
      onSuccess({ token, userId, role:"teacher", name:form.name, email:form.email });
    } catch(e) {
      setError(e.message?.toLowerCase().includes("already") ? "An account with this email already exists. Please sign in." : e.message || "Something went wrong.");
    }
    setLoading(false);
  };

  if (showConfirm) return <EmailConfirmScreen email={form.email} onSwitch={onSwitch} onBack={() => setShowConfirm(false)} mode="teacher" />;

  return (
    <div className="auth-right-panel" style={{ padding:"32px 28px", overflowY:"auto", maxHeight:"100vh", background:"#FFF1D5" }}>
      <div style={{ marginBottom:24 }}>
        <p style={{ fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:6 }}>Join as a Yoga Guru</p>
        <h1 className="serif" style={{ fontSize:28, fontWeight:300, color:"#2E2A24" }}>Create your Yoga Guru account</h1>
      </div>
      {error && <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:6, padding:"12px 16px", marginBottom:16, color:"#C0392B", fontSize:13, display:"flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} style={{flexShrink:0}}/>{error}</div>}
      <button className="btn-google" onClick={() => supabase.auth.signInWithGoogle()}><GoogleIcon /> Continue with Google</button>
      <Divider />
      <Field label="Full Name">
        <input placeholder="Your full name" value={form.name} onChange={e => set("name",e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Email Address">
        <input type="email" placeholder="you@example.com" value={form.email} onChange={e => set("email",e.target.value)} style={inputStyle} />
      </Field>
      <Field label="Password">
        <PasswordInput value={form.password} onChange={e => set("password",e.target.value)} placeholder="Minimum 6 characters" onKeyDown={e => e.key==="Enter"&&handleSignUp()} style={inputStyle} />
      </Field>
      <button className="btn-primary" onClick={handleSignUp} disabled={loading}>{loading?<><div className="spinner"/>Creating Account...</>:"Create Teacher Account →"}</button>
      <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid rgba(181,112,63,0.15)", textAlign:"center" }}>
        <p style={{ fontSize:13, color:"#6b6b7a" }}>Already have an account?{" "}<button onClick={() => onSwitch("login")} style={{ background:"none", border:"none", cursor:"pointer", color:"#985A30", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500, minHeight:44 }}>Sign In</button></p>
      </div>
    </div>
  );
}

// ─── Reusable password input with show/hide toggle ────────────────────────────
function PasswordInput({ value, onChange, placeholder, onKeyDown, style, borderColor }) {
  const [show, setShow] = useState(false);
  const eyeOpen  = "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z";
  const eyeOff   = ["M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94","M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19","M1 1l22 22","M6.58 6.58A11.1 11.1 0 004 12s4 8 8 8a11.1 11.1 0 005.42-1.42"];
  return (
    <div style={{ position:"relative" }}>
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        style={{ ...style, paddingRight:44, borderColor: borderColor || "rgba(181,112,63,0.3)" }}
      />
      <button type="button" onClick={() => setShow(s => !s)}
        style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", padding:4, display:"flex", alignItems:"center", color:"#6b6b7a" }}>
        <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          {show
            ? (Array.isArray(eyeOff) ? eyeOff.map((d,i)=><path key={i} d={d}/>) : <path d={eyeOff}/>)
            : <path d={eyeOpen}/>}
        </svg>
      </button>
    </div>
  );
}

function Login({ onSwitch, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [form, setForm] = useState({ email:"", password:"" });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const inputStyle = { width:"100%", padding:"12px 16px", border:"1.5px solid rgba(181,112,63,0.3)", borderRadius:6, fontSize:16, background:"white", fontFamily:"'Jost',sans-serif", outline:"none" };

  const handleLogin = async () => {
    if (!form.email || !form.password) { setError("Please enter your email and password."); return; }
    setLoading(true); setError("");
    try {
      const data = await supabase.auth.signIn({ email:form.email, password:form.password });
      // Same shape problem as signup, with a worse ending: a failed sign-in has
      // no `error` key, so this guard never fired, token and userId came back
      // undefined, and the handler carried on to onSuccess — signing the person
      // "in" to a session with no token at all.
      {
        const m = authErrorMessage(data);
        if (m) { setError(m); setLoading(false); return; }
      }
      const token = data.access_token;
      const userId = data.user?.id;
      if (!token || !userId) {
        setError("Something went wrong signing in. Please try again.");
        setLoading(false); return;
      }
      const res = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`, {
        headers: { apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` }
      });
      const profiles = await res.json();
      const profile = Array.isArray(profiles) ? profiles[0] : null;
      onSuccess({ token, userId, role:profile?.role||"student", name:profile?.full_name||form.email, email:form.email, rememberMe });
    } catch { setError("Invalid email or password."); }
    setLoading(false);
  };

  return (
    <div className="auth-right-panel" style={{ padding:"32px 28px", background:"#FFF1D5" }}>
      <div style={{ marginBottom:28 }}>
        <p style={{ fontSize:12, fontWeight:600, color:"#4a4a5a", marginBottom:6 }}>Welcome Back</p>
        <h1 className="serif" style={{ fontSize:28, fontWeight:300, color:"#2E2A24" }}>Sign In to YogaStay</h1>
      </div>
      {error && (
        <div style={{ background:error.includes("confirm")?"rgba(181,112,63,0.1)":"#fef2f2", border:`1px solid ${error.includes("confirm")?"rgba(181,112,63,0.3)":"#fecaca"}`, borderRadius:6, padding:"12px 16px", marginBottom:16, color:error.includes("confirm")?"#985A30":"#C0392B", fontSize:13 }}>
          {error.includes("confirm")?<Mail size={14} style={{flexShrink:0}}/>:<AlertTriangle size={14} style={{flexShrink:0}}/>} {error}
        </div>
      )}
      <button className="btn-google" onClick={() => supabase.auth.signInWithGoogle()}><GoogleIcon /> Continue with Google</button>
      <Divider />
      <Field label="Email Address">
        <input type="email" placeholder="you@example.com" value={form.email} onChange={e => set("email",e.target.value)} onKeyDown={e => e.key==="Enter"&&handleLogin()} style={inputStyle} />
      </Field>
      <Field label="Password">
        <PasswordInput value={form.password} onChange={e => set("password",e.target.value)} placeholder="Your password" onKeyDown={e => e.key==="Enter"&&handleLogin()} style={inputStyle} />
      </Field>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
        <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none" }}>
          <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
            style={{ width:16, height:16, accentColor:"#B5703F", cursor:"pointer" }} />
          <span style={{ fontSize:13, color:"#4a4a5a" }}>Remember me</span>
        </label>
      </div>
      <button className="btn-primary" onClick={handleLogin} disabled={loading}>{loading?<><div className="spinner"/>Signing In...</>:"Sign In →"}</button>
      <div style={{ marginTop:24, paddingTop:16, borderTop:"1px solid rgba(181,112,63,0.15)" }}>
        <p style={{ fontSize:13, color:"#6b6b7a", textAlign:"center", marginBottom:12 }}>New to YogaStay?</p>
        <div style={{ display:"grid", gridTemplateColumns: INDEPENDENT_TEACHERS ? "1fr 1fr 1fr" : "1fr 1fr", gap:10 }}>
          <button className="btn-ghost" onClick={() => onSwitch("school-signup")} style={{ fontSize:11 }}>Register School</button>
          {INDEPENDENT_TEACHERS && <button className="btn-ghost" onClick={() => onSwitch("teacher-signup")} style={{ fontSize:11 }}>Yoga Guru</button>}
          <button className="btn-ghost" onClick={() => onSwitch("student-signup")} style={{ fontSize:11 }}>Join as Student</button>
        </div>
      </div>
    </div>
  );
}

function MobileMenu({ onSwitch, onClose }) {
  return (
    <div className="mobile-menu open" style={{ position:"fixed", inset:0, background:"white", zIndex:500, flexDirection:"column", padding:"0", overflowY:"auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:"1px solid rgba(181,112,63,0.15)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", background:"#B5703F", display:"flex", alignItems:"center", justifyContent:"center" }}><YogaIcon size={18} color="white"/></div>
          <span className="serif" style={{ color:"#2E2A24", fontSize:20, fontWeight:400 }}>YogaStay</span>
        </div>
        <CloseButton onClick={onClose} size={34} />
      </div>
      <div style={{ padding:"20px" }}>
        {[[<YogaIcon size={22} color="#B5703F"/>,"Find Retreats","browse"],[<GraduationCap size={22} color="#B5703F"/>,"Yoga Teacher Training","teacher-training"],[<BookOpen size={22} color="#B5703F"/>,"List Your School","school-signup"],...(INDEPENDENT_TEACHERS ? [[<GraduationCap size={22} color="#B5703F"/>,"Join as a Yoga Guru","teacher-signup"]] : []),[<Lock size={22} color="#B5703F"/>,"Sign In","login"]].map(([icon, label, screen]) => (
          <button key={screen} onClick={() => { onSwitch(screen); onClose(); }} style={{ width:"100%", padding:"16px 20px", background:"none", border:"none", borderRadius:10, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:15, color:"#2E2A24", textAlign:"left", display:"flex", alignItems:"center", gap:14, marginBottom:4, minHeight:56 }}
            onMouseEnter={e => e.currentTarget.style.background="rgba(181,112,63,0.08)"}
            onMouseLeave={e => e.currentTarget.style.background="none"}>
            <span style={{ display:"flex", alignItems:"center" }}>{icon}</span>
            {label}
          </button>
        ))}
        <div style={{ borderTop:"1px solid rgba(181,112,63,0.15)", marginTop:16, paddingTop:16 }}>
          <button onClick={() => { onSwitch("browse"); onClose(); }} style={{ width:"100%", padding:"16px", background:"#B5703F", border:"none", borderRadius:8, cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:13, letterSpacing:2, textTransform:"uppercase", color:"white", fontWeight:500, minHeight:52 }}>
            Get Started →
          </button>
        </div>
      </div>
    </div>
  );
}

// Trust-bar icons, keyed by policies.TRUST_ITEMS[].key. Copy lives in the module.
const TRUST_ICON_PATHS = {
  verified: "M9 12l2 2 4-4M22 12A10 10 0 112 12a10 10 0 0120 0z",
  enquiry:  ["M4 4h16v16H4z", "M22 6l-10 7L2 6"],
  direct:   ["M20 6L9 17l-5-5"],
};

const HOME_DESTINATIONS = ["Rishikesh","Bali","Kerala","Goa","Mysore","Chiang Mai","Ubud","Dharamsala"];

// ─── QUOTE CAROUSEL ──────────────────────────────────────────────────────────
const FALLBACK_QUOTES = [
  { text: "Yoga is the journey of the self, through the self, to the self.", author: "The Bhagavad Gita" },
  { text: "Yoga does not just change the way we see things, it transforms the person who sees.", author: "B.K.S. Iyengar" },
  { text: "Yoga is a light, which once lit will never dim. The better your practice, the brighter the flame.", author: "B.K.S. Iyengar" },
  { text: "Inhale the future, exhale the past.", author: "Unknown" },
  { text: "Yoga is not about touching your toes, it's about what you learn on the way down.", author: "Jigar Gor" },
  { text: "The most important pieces of equipment you need for doing yoga are your body and your mind.", author: "Rodney Yee" },
  { text: "The very heart of yoga practice is steady effort in the direction you want to go.", author: "Sally Kempton" },
  { text: "Yoga is the perfect opportunity to be curious about who you are.", author: "Jason Crandell" },
  { text: "Yoga begins right where I am — not where I was yesterday or where I long to be.", author: "Linda Sparrowe" },
  { text: "The nature of yoga is to shine the light of awareness into the darkest corners of the body.", author: "Jason Crandell" },
];

function QuoteCarousel() {
  const [quotes, setQuotes] = useState(FALLBACK_QUOTES);
  const [idx, setIdx]       = useState(0);
  const [fade, setFade]     = useState(true);

  useEffect(() => {
    fetch(`${SUPABASE_URL}/rest/v1/platform_settings?key=eq.landing_quotes&select=value`, {
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    })
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d) && d[0]?.value) {
          try { const q = JSON.parse(d[0].value); if (q.length) setQuotes(q); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % quotes.length);
        setFade(true);
      }, 400);
    }, 6000);
    return () => clearInterval(timer);
  }, [quotes.length]);

  const q = quotes[idx];
  return (
    <div style={{ background:"linear-gradient(135deg,#2E2A24,#3a3a5a)", padding:"36px 24px" }}>
      <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center", transition:"opacity 0.4s ease", opacity:fade?1:0 }}>
        <div style={{ fontSize:24, color:"rgba(181,112,63,0.5)", lineHeight:1, marginBottom:10, fontFamily:"Georgia,serif" }}>"</div>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(17px,2.5vw,22px)", fontWeight:300, color:"rgba(255,255,255,0.92)", lineHeight:1.7, fontStyle:"italic", margin:"0 0 16px" }}>
          {q?.text}
        </p>
        <div style={{ fontSize:12, color:"rgba(181,112,63,0.8)", letterSpacing:0.5 }}>
          — {q?.author}
        </div>
        {/* Dot indicators */}
        <div style={{ display:"flex", justifyContent:"center", gap:6, marginTop:20 }}>
          {quotes.map((_, i) => (
            <button key={i} onClick={() => { setFade(false); setTimeout(()=>{ setIdx(i); setFade(true); },200); }}
              style={{ width: i===idx?20:6, height:6, borderRadius:3, background:i===idx?"#B5703F":"rgba(181,112,63,0.3)", border:"none", cursor:"pointer", transition:"all 0.3s", padding:0 }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Landing({ onSwitch, onBrowse, search, setSearch, filterStyle, setFilterStyle, selectedMonth, setSelectedMonth, loggedInUser, onGoToDashboard }) {
  const [visitor] = useVisitorCurrency();
  const rates = useFxRates();
  const [menuOpen, setMenuOpen] = useState(false);
  const [featuredSchools, setFeaturedSchools] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [nlEmail, setNlEmail]   = useState("");
  const [nlState, setNlState]   = useState("idle"); // idle | loading | success | error | duplicate

  const handleSubscribe = async () => {
    const email = nlEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNlState("error"); return;
    }
    setNlState("loading");
    try {
      // Check for duplicate first
      const check = await fetch(`${SUPABASE_URL}/rest/v1/newsletter_subscribers?email=eq.${encodeURIComponent(email)}&select=id`, {
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
      });
      const existing = await check.json();
      if (Array.isArray(existing) && existing.length > 0) { setNlState("duplicate"); return; }

      const res = await fetch(`${SUPABASE_URL}/rest/v1/newsletter_subscribers`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          Prefer: "return=minimal",
        },
        body: JSON.stringify({ email, subscribed_at: new Date().toISOString(), source: "landing_footer" }),
      });
      setNlState(res.ok ? "success" : "error");
    } catch { setNlState("error"); }
  };

  // Dynamic SEO for landing page
  useEffect(() => {
    document.title = "YogaStay — Book Yoga Classes & Retreats Worldwide";
    track(EVENTS.HOME_VIEW);
    document.querySelector('meta[name="description"]')?.setAttribute("content",
      "Find yoga schools and retreats worldwide. Browse Hatha, Vinyasa, Yin, Kundalini and more, send a free enquiry and pay the school directly."
    );
  }, []);

  // Public schools only (approved or verified — RLS decides), verified first,
  // and no invented fallback: when the fetch fails or
  // returns nothing the section is hidden. (It used to fill in six made-up
  // schools with made-up ratings — "Ananda Yoga Ashram ★4.9, 124 reviews".)
  // The same rows feed the destination counts below.
  const [destCounts, setDestCounts] = useState(null); // { [destName]: { schools, programmes } }
  const [featuredSummary, setFeaturedSummary] = useState({}); // school_id → price / next start (T1)
  const [liveDestinations, setLiveDestinations] = useState([]); // [{ name, count }] for the search bar
  const [heroScope, setHeroScope] = useState("all");            // All / Retreats / Teacher Training / Classes
  useEffect(() => {
    const H = { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` } };
    const load = async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/rest/v1/schools?select=id,name,location,city,country,style,photo_url,verified,rating,review_count&is_hidden=not.is.true&order=verified.desc,created_at.desc&limit=200`, H);
        const data = await res.json();
        const verified = Array.isArray(data) ? data : [];
        setFeaturedSchools(verified.slice(0, 6));
        // Places, busiest first, then countries — so the search bar's "Popular"
        // row and destination menu only offer places that have a school.
        const tally = (key) => { const acc = {}; verified.forEach(v => { const k = key(v); if (k) acc[k] = (acc[k] || 0) + 1; }); return Object.entries(acc).sort((a, b) => b[1] - a[1]).map(([name, count]) => ({ name, count })); };
        const places = tally(v => (v.city || String(v.location || "").split(",")[0] || "").trim());
        const countries = tally(v => (v.country || "").trim()).filter(c => !places.some(p => p.name === c.name));
        setLiveDestinations([...places, ...countries]);
        const today = new Date().toISOString().slice(0, 10);
        const [r, c] = await Promise.all([
          fetch(`${SUPABASE_URL}/rest/v1/retreats?select=school_id,location,region,country,price,early_bird_price,early_bird_deadline,currency,start_date,end_date,duration_days,availability_type,available_from,available_until,arrival_days,price_options,is_active,is_draft,is_hidden&is_active=eq.true&is_draft=not.is.true&is_hidden=not.is.true&${liveRetreatFilter(today)}&limit=1000`, H),
          fetch(`${SUPABASE_URL}/rest/v1/classes?select=school_id,price,currency,is_free,is_active,is_draft,end_date&is_active=eq.true&limit=2000`, H),
        ]);
        const rows = await r.json();
        const cls = await c.json().catch(() => []);
        setDestCounts(countByDestination(verified, Array.isArray(rows) ? rows : [], HOME_DESTINATIONS));
        setFeaturedSummary(summarizeProgrammes({ retreats: Array.isArray(rows) ? rows : [], classes: Array.isArray(cls) ? cls : [], toUsd: (a, c2) => toUsdLive(a, c2), today }));
      } catch {
        setFeaturedSchools([]);
      }
      setFeaturedLoading(false);
    };
    load();
  }, []);

  const destinations = [
    { name:"Rishikesh", country:"India",     guide:"/yoga/rishikesh/", tag:"Yoga Capital",   styles:["Hatha","Meditation"],       img:"https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&q=80" },
    { name:"Bali",      country:"Indonesia", guide:"/yoga/bali/",      tag:"Island Retreats",   styles:["Vinyasa","Hatha"],          img:"https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&q=80" },
    { name:"Kerala",    country:"India",     guide:"/yoga/kerala/",    tag:"Ayurveda Hub",   styles:["Yin","Restorative"],        img:"https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=400&q=80" },
    { name:"Goa",       country:"India",     guide:"/yoga/goa/",       tag:"Beach Yoga",     styles:["Vinyasa","Ashtanga"],       img:"https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=400&q=80" },
    { name:"Mysore",    country:"India",     guide:"/yoga/mysore/",    tag:"Ashtanga Source",styles:["Ashtanga"],                 img:"https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=400&q=80" },
    { name:"Chiang Mai",country:"Thailand",  tag:"Spiritual",      styles:["Meditation","Yin"],         img:"https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=400&q=80" },
    { name:"Ubud",      country:"Indonesia", guide:"/yoga/bali/",      tag:"Sacred Valley",  styles:["Hatha","Kundalini"],        img:"https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=400&q=80" },
    { name:"Dharamsala",country:"India",     tag:"Himalayan",      styles:["Meditation","Hatha"],       img:"https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=400&q=80" },
  ];

  const STYLE_DOT_COLORS = { Hatha:"#F59E0B", Vinyasa:"#EF4444", Yin:"#818CF8", Kundalini:"#A855F7", Meditation:"#3B82F6", Ashtanga:"#10B981" };
  const yogaStyles = [
    { style:"Hatha", desc:"Foundation & balance" },
    { style:"Vinyasa", desc:"Flow & movement" },
    { style:"Yin", desc:"Deep & restorative" },
    { style:"Kundalini", desc:"Energy & awakening" },
    { style:"Meditation", desc:"Stillness & clarity" },
    { style:"Ashtanga", desc:"Discipline & strength" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#FAF7F1" }}>

      {menuOpen && <MobileMenu onSwitch={(s)=>{ if(s==="browse"){onBrowse("");} else if(s==="teacher-training"){onBrowse("", "teacher-training");} else onSwitch(s); }} onClose={() => setMenuOpen(false)} />}

      {/* NAV */}
      <nav className="landing-nav" style={{ background:"white", borderBottom:"1px solid rgba(181,112,63,0.15)", padding:"0 40px", height:64, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div className="landing-nav-logo" onClick={() => window.scrollTo({ top:0, behavior:"smooth" })} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
          <div style={{ width:34, height:34, borderRadius:"50%", background:"#B5703F", display:"flex", alignItems:"center", justifyContent:"center" }}><YogaIcon size={20} color="white"/></div>
          <span className="serif" style={{ color:"#2E2A24", fontSize:22, fontWeight:400 }}>YogaStay</span>
        </div>
        {/* Desktop nav */}
        <div className="hide-mobile" style={{ display:"flex", gap:24, alignItems:"center" }}>
          <button onClick={() => onBrowse("", "retreats")} style={{ background:"none", border:"none", cursor:"pointer", color:"#4a4a5a", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500 }}>Retreats</button>
          <button onClick={() => onBrowse("", "teacher-training")} style={{ background:"none", border:"none", cursor:"pointer", color:"#4a4a5a", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500 }}>Yoga Teacher Training</button>
          <button onClick={() => onBrowse("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#4a4a5a", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500 }}>Schools</button>
          <button onClick={() => document.getElementById("destinations-section")?.scrollIntoView({ behavior:"smooth" })} style={{ background:"none", border:"none", cursor:"pointer", color:"#4a4a5a", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500 }}>Destinations</button>
          {INDEPENDENT_TEACHERS && <button onClick={() => onBrowse("", "teachers")} style={{ background:"none", border:"none", cursor:"pointer", color:"#4a4a5a", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500 }}>Yoga Gurus</button>}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {loggedInUser ? (
            <button className="hide-mobile" onClick={onGoToDashboard} style={{ background:"#B5703F", border:"none", color:"white", padding:"8px 18px", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:13, borderRadius:20, fontWeight:500 }}>My Dashboard →</button>
          ) : (<>
            <button className="hide-mobile" onClick={() => onSwitch("login")} style={{ background:"none", border:"1px solid rgba(181,112,63,0.4)", color:"#4a4a5a", padding:"8px 16px", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:13, letterSpacing:0, borderRadius:20 }}>Sign In</button>
            <button className="hide-mobile" onClick={() => onSwitch("school-signup")} style={{ background:"#B5703F", border:"none", color:"white", padding:"8px 16px", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:13, letterSpacing:0, borderRadius:20, fontWeight:500 }}>List School</button>
          </>)}
          {/* Mobile hamburger */}
          <button className="mobile-nav-btn" onClick={() => setMenuOpen(true)} style={{ display:"none", background:"none", border:"none", cursor:"pointer", color:"#2E2A24", minHeight:44, minWidth:44, alignItems:"center", justifyContent:"center" }}><Menu size={22}/></button>
        </div>
      </nav>

      {/* HERO */}
      <div className="hero-container" style={{ position:"relative", height:580, overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center" }}>
        {/* LCP element. srcset/sizes MUST stay identical to the preload in
              public/index.html, or the browser picks a different candidate here
              and fetches the image a second time. A fixed w=1600 was being sent
              to phones: 250KB where 828w AVIF is 54KB. */}
          <img
            src="https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=828&q=75&auto=format"
            srcSet="https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=640&q=75&auto=format 640w, https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=828&q=75&auto=format 828w, https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=1280&q=75&auto=format 1280w, https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=1920&q=75&auto=format 1920w"
            sizes="100vw"
            fetchpriority="high" decoding="async"
            alt="Yoga retreat at sunrise"
            style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover" }} />
        <div style={{ position:"absolute", inset:0, background:"linear-gradient(to bottom,rgba(46,42,36,0.35),rgba(46,42,36,0.6))" }} />
        <div style={{ position:"relative", width:"100%", maxWidth:760, margin:"0 auto", padding:"0 16px", textAlign:"center" }}>
          <p style={{ color:"rgba(255,255,255,0.85)", fontSize:11, letterSpacing:4, textTransform:"uppercase", marginBottom:14 }}>Unplug · Practice · Transform</p>
          <h1 className="hero-title serif" style={{ color:"white", fontSize:"clamp(28px,5vw,62px)", fontWeight:300, marginBottom:16, lineHeight:1.08, textShadow:"0 2px 24px rgba(0,0,0,0.35)", overflowWrap:"break-word", maxWidth:"100%" }}>
            Discover handpicked yoga<br className="hero-br" />schools &amp; retreats worldwide
          </h1>
          <p className="hero-subtitle" style={{ color:"rgba(255,255,255,0.85)", fontSize:16, marginBottom:36, maxWidth:480, margin:"0 auto 36px" }}>
            Yoga schools across India, Bali, Thailand & beyond — send a free enquiry and pay the school directly
          </p>

          {/* SEARCH BOX */}
          <SearchBar
            search={search}
            setSearch={setSearch}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            filterStyle={filterStyle}
            setFilterStyle={setFilterStyle}
            onSearch={() => onBrowse(search, heroScope === "teacher_training" ? "teacher-training" : heroScope === "retreats" ? "retreats" : "browse")}
            destinations={liveDestinations}
            variant="hero"
            scope={heroScope} scopeOptions={SCOPE_OPTIONS} onScopeChange={setHeroScope}
            onTeacherTrainingQuiz={a => onBrowse("", "teacher-training", "All", "Any Duration", null, { hours: (a.certification || "").match(/\d+/)?.[0] || "", region: a.location })}
          />

          {/* L2: one "Popular" row only — the search bar's, fed from live
              destinations below. A second, hard-coded row used to sit here. */}
        </div>
      </div>

      {/* TRUST BAR */}
      <div className="trust-bar" style={{ display:"flex", justifyContent:"center", gap:40, padding:"20px 24px", background:"#FAF7F1", borderBottom:"1px solid rgba(181,112,63,0.12)" }}>
        {TRUST_ITEMS.map(t => ({ ...t, path: TRUST_ICON_PATHS[t.key] })).map(({ path, label, sub }) => (
          <div key={label} style={{ display:"flex", alignItems:"center", gap:10 }}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#B5703F" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              {Array.isArray(path) ? path.map((d,i)=><path key={i} d={d}/>) : <path d={path}/>}
            </svg>
            <div>
              <div style={{ fontSize:12, fontWeight:600, color:"#2E2A24" }}>{label}</div>
              <div style={{ fontSize:11, color:"#6b6b7a" }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="main-content" style={{ maxWidth:1100, margin:"0 auto", padding:"48px 24px" }}>

        {/* BROWSE BY CATEGORY */}
        <div className="section-mb" style={{ marginBottom:52 }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4 }}>What are you seeking?</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:20 }}>Browse retreats and trainings by what matters to you</p>
          <div className="category-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14 }}>
            {[
              { label:"Yoga Retreats",     desc:"Practice & restore",     img:"https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=500&q=80", q:"" },
              { label:"Meditation",        desc:"Stillness & clarity",    img:"https://images.unsplash.com/photo-1528319725582-ddc096101511?w=500&q=80", style:"Meditation" },
              { label:"Yoga Teacher Training",  desc:"Become certified",       img:"https://images.unsplash.com/photo-1592432678016-e910b452f9a2?w=500&q=80", tab:"teacher-training" },
              { label:"Women's Retreats",  desc:"Circles & community",    img:"https://images.unsplash.com/photo-1599447421416-3414500d18a5?w=500&q=80", q:"women" },
              { label:"Detox & Ayurveda",  desc:"Cleanse & heal",         img:"https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=500&q=80", q:"ayurveda" },
              { label:"Yin & Restorative", desc:"Slow & deep",            img:"https://images.unsplash.com/photo-1545389336-cf090694435e?w=500&q=80", style:"Yin" },
            ].map((c,i) => (
              <button key={i} onClick={() => onBrowse(c.q ?? "", c.tab || "browse", c.style || "All")}
                aria-label={`Browse ${c.label}`}
                style={{ border:"none", padding:0, borderRadius:12, overflow:"hidden", position:"relative", height:170, cursor:"pointer", transition:"all 0.2s", boxShadow:"0 2px 12px rgba(46,42,36,0.08)", textAlign:"left" }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 12px 32px rgba(181,112,63,0.22)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 12px rgba(46,42,36,0.08)"; }}>
                <img src={c.img} alt={c.label} loading="lazy" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(46,42,36,0.78),rgba(46,42,36,0.05) 60%)" }} />
                <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"14px 16px", color:"white" }}>
                  <div className="serif" style={{ fontSize:19, fontWeight:500, marginBottom:2 }}>{c.label}</div>
                  <div style={{ fontSize:12, opacity:0.85 }}>{c.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* FEATURED SCHOOLS — hidden when there are none to show */}
        {(featuredLoading || featuredSchools.length > 0) && <div className="section-mb" style={{ marginBottom:52 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end", marginBottom:20 }}>
            <div>
              <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4 }}>Featured schools</h2>
              <p style={{ color:"#6b6b7a", fontSize:14 }}>Every school is manually reviewed by our team</p>
            </div>
            <button onClick={() => onBrowse("")} style={{ background:"none", border:"none", cursor:"pointer", color:"#B5703F", fontFamily:"'Jost',sans-serif", fontSize:13, fontWeight:500, whiteSpace:"nowrap" }}>View all →</button>
          </div>
          <div className="featured-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20 }}>
            {featuredLoading ? (
              // Skeleton loading — 6 cards
              [1,2,3,4,5,6].map(i=>(
                <div key={i} style={{ borderRadius:12, overflow:"hidden", background:"white", boxShadow:"0 2px 12px rgba(181,112,63,0.08)" }}>
                  <div className="featured-card-img" style={{ height:190, background:"linear-gradient(90deg,#f0f4ff 25%,#e8edff 50%,#f0f4ff 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }} />
                  <div style={{ padding:"14px 16px 16px" }}>
                    {[40,90,60].map((w,j)=><div key={j} style={{ height:12, background:"#f0f4ff", borderRadius:4, marginBottom:10, width:`${w}%`, animation:"shimmer 1.4s infinite" }} />)}
                  </div>
                </div>
              ))
            ) : featuredSchools.map((s, i) => {
              const tag  = s._tag  || (s.verified ? "Verified" : "");
              const loc  = s.location || `${s.city||""}${s.city&&s.country?", ":""}${s.country||""}`;
              const img  = s.photo_url || "https://images.unsplash.com/photo-1545389336-cf090694435e?w=600&q=80";
              const rd   = ratingDisplay(s.rating, s.review_count);
              return (
                <div key={s.id||i} onClick={() => { track(EVENTS.SCHOOL_CARD_CLICKED, { school_id: s.id, source: "home_featured", position: i + 1 }); onBrowse("", "browse", "All", "Any Duration", s.id); }}
                  style={{ borderRadius:12, overflow:"hidden", background:"white", boxShadow:"0 2px 16px rgba(181,112,63,0.12)", cursor:"pointer", transition:"all 0.2s" }}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 12px 40px rgba(181,112,63,0.25)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 2px 16px rgba(181,112,63,0.12)";}}>
                  <div className="featured-card-img" style={{ position:"relative", height:190, overflow:"hidden" }}>
                    {/* width/height let the browser reserve the box before the
                        image lands (no layout shift) and decode off the main
                        thread. They do not change the rendered size — the style
                        block still drives that. */}
                    <img src={img} alt={s.name} width={340} height={190} loading="lazy" decoding="async"
                      loading={i < 2 ? "eager" : "lazy"} decoding="async"
                      style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                      onError={e=>{ e.target.src="https://images.unsplash.com/photo-1545389336-cf090694435e?w=600&q=80"; }} />
                    <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,0.35),transparent 60%)" }} />
                    {tag && (
                      tag === "Verified"
                        ? <div style={{ position:"absolute", top:10, left:10 }}><VerifiedBadge /></div>
                        : <div style={{ position:"absolute", top:10, left:10, background:"#B5703F", color:"white", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", padding:"4px 10px", borderRadius:20, fontWeight:600 }}>{tag}</div>
                    )}
                    <div style={{ position:"absolute", top:10, right:10, background:"rgba(255,255,255,0.95)", borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:600, color:"#2E2A24" }}>{rd.show ? `★ ${rd.rating.toFixed(1)}` : NEW_LABEL}</div>
                  </div>
                  <div style={{ padding:"14px 16px 16px" }}>
                    <p style={{ fontSize:11, color:"#B5703F", letterSpacing:1.5, textTransform:"uppercase", marginBottom:4 }}>{(s.style||[]).slice(0,2).join(" · ") || "Yoga"}</p>
                    <h3 style={{ fontSize:15, fontWeight:600, color:"#2E2A24", marginBottom:4 }}>{s.name}</h3>
                    <p style={{ fontSize:13, color:"#6b6b7a", marginBottom:10, display:"flex", alignItems:"center", gap:4 }}><MapPin size={12} style={{flexShrink:0}}/>{loc}</p>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:10, borderTop:"1px solid rgba(181,112,63,0.1)" }}>
                      {(() => {
                        const ps = featuredSummary[s.id];
                        const price = ps?.fromAmount != null ? `From ${fmtIn(ps.fromAmount, ps.fromCurrency, visitor, rates)} / ${ps.fromUnit}` : ps?.freeClasses ? "Free classes" : "Price on request";
                        const meta = [ps?.nextStart && `Next start ${new Date(ps.nextStart + "T00:00:00").toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })}`, durationLabel(ps)].filter(Boolean).join(" · ");
                        return <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:13, fontWeight:600, color:"#2E2A24" }}>{price}</div>
                          {meta && <div style={{ fontSize:11.5, color:"#6b6b7a", marginTop:2 }}>{meta}</div>}
                        </div>;
                      })()}
                      <span style={{ fontSize:12, color:"#B5703F", fontWeight:600, fontFamily:"'Jost',sans-serif", letterSpacing:1, textTransform:"uppercase", whiteSpace:"nowrap" }}>View →</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>}

        {/* HOW IT WORKS */}
        <div className="section-mb" style={{ marginBottom:52, background:"var(--sage-soft, #EDF0E8)", borderRadius:16, padding:"36px 32px" }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4, textAlign:"center" }}>How YogaStay Works</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:28, textAlign:"center" }}>From searching to your mat, in three simple steps</p>
          <div className="how-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:20 }}>
            {[
              { n:"1", title:"Discover", desc:"Browse schools and retreats by destination, style, dates and budget." },
              { n:"2", title:"Send a free enquiry", desc:ENQUIRY_FLOW_FULL },
              { n:"3", title:"Show up & practice", desc:"Arrive, settle into your accommodation, and deepen your practice with trusted teachers." },
            ].map(s => (
              <div key={s.n} style={{ textAlign:"center", padding:"8px 12px" }}>
                <div style={{ width:48, height:48, borderRadius:"50%", background:"#B5703F", color:"white", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px", fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:500 }}>{s.n}</div>
                <h3 style={{ fontSize:17, fontWeight:600, color:"#2E2A24", marginBottom:6 }}>{s.title}</h3>
                <p style={{ fontSize:13, color:"#6b6b7a", lineHeight:1.7 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* BROWSE BY DURATION */}
        <div className="section-mb" style={{ marginBottom:52 }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4 }}>Browse by Duration</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:20 }}>Find a retreat that fits your schedule</p>
          <div className="duration-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:14 }}>
            {[
              { label:"Weekend Retreat", days:"2-3 days", duration:"Weekend",  img:"https://images.unsplash.com/photo-1545389336-cf090694435e?w=400&q=80" },
              { label:"One Week",        days:"7 days",   duration:"1 Week",   img:"https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=400&q=80" },
              { label:"Two Weeks",       days:"14 days",  duration:"2 Weeks",  img:"https://images.unsplash.com/photo-1599901860904-17e6ed7083a0?w=400&q=80" },
              { label:"Monthly Immersion", days:"28+ days", duration:"1 Month+", img:"https://images.unsplash.com/photo-1588286840104-8957b019727f?w=400&q=80" },
            ].map((r,i) => (
              <div key={i} onClick={() => onBrowse("", "retreats", "All", r.duration)} style={{ borderRadius:8, overflow:"hidden", position:"relative", height:150, cursor:"pointer", transition:"all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(181,112,63,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="none"; }}>
                <img src={r.img} alt={r.label} loading="lazy" decoding="async" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,0.65),transparent 60%)" }} />
                <div style={{ position:"absolute", bottom:0, left:0, padding:"12px 14px", color:"white" }}>
                  <div style={{ fontWeight:500, fontSize:14, marginBottom:2 }}>{r.label}</div>
                  <div style={{ fontSize:11, opacity:0.75 }}>{r.days}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* EXPLORE DESTINATIONS */}
        <div id="destinations-section" className="section-mb" style={{ marginBottom:52 }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4 }}>Explore Destinations</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:20 }}>Sacred yoga locations worldwide</p>
          <div className="destination-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:14 }}>
            {destinations.map((dest,i) => {
              const c = destCounts?.[dest.name];
              const countLabel = c ? destinationCountLabel(c.schools, c.programmes) : null;
              return (
              <div key={i} onClick={() => { if (dest.guide && !(c?.schools)) window.location.href = dest.guide; else onBrowse(dest.name); }}
                style={{ borderRadius:12, overflow:"hidden", position:"relative", height:170, cursor:"pointer", transition:"all 0.22s", boxShadow:"0 2px 10px rgba(0,0,0,0.08)" }}
                onMouseEnter={e => { e.currentTarget.style.transform="translateY(-4px)"; e.currentTarget.style.boxShadow="0 12px 32px rgba(181,112,63,0.3)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.boxShadow="0 2px 10px rgba(0,0,0,0.08)"; }}>
                <img src={dest.img} alt={dest.name} loading="lazy" decoding="async" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                <div style={{ position:"absolute", inset:0, background:"linear-gradient(to top,rgba(0,0,0,0.72),rgba(0,0,0,0.1) 55%)" }} />
                {dest.tag && (
                  <div style={{ position:"absolute", top:10, left:10, background:"rgba(181,112,63,0.85)", backdropFilter:"blur(4px)", color:"white", fontSize:9, letterSpacing:1.5, textTransform:"uppercase", padding:"3px 8px", borderRadius:20, fontWeight:600 }}>{dest.tag}</div>
                )}
                <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"12px 14px", color:"white" }}>
                  <div style={{ fontWeight:600, fontSize:14, marginBottom:3 }}>{dest.name}, {dest.country}</div>
                  {countLabel && <div style={{ fontSize:11, opacity:0.85 }}>{countLabel}</div>}
                  <div style={{ display:"flex", gap:5, marginTop:6, flexWrap:"wrap" }}>
                    {(dest.styles||[]).map(s => (
                      <span key={s} style={{ fontSize:9, padding:"1px 7px", borderRadius:20, background:"rgba(255,255,255,0.18)", border:"1px solid rgba(255,255,255,0.3)", letterSpacing:0.8, textTransform:"uppercase" }}>{s}</span>
                    ))}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>

        {/* BROWSE BY STYLE */}
        <div className="section-mb" style={{ marginBottom:52 }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4 }}>Browse by Style</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:20 }}>Find the practice that calls to you</p>
          <div className="styles-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:10 }}>
            {yogaStyles.map(({ style, desc }) => (
              <div key={style} onClick={() => onBrowse("", "browse", style)}
                style={{ padding:"18px 14px", border:"1.5px solid rgba(181,112,63,0.2)", borderRadius:8, cursor:"pointer", textAlign:"center", transition:"all 0.15s", background:"white" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor="#B5703F"; e.currentTarget.style.background="#f8f9ff"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor="rgba(181,112,63,0.2)"; e.currentTarget.style.background="white"; }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}>
                  <span style={{ width:28, height:28, borderRadius:"50%", background:STYLE_DOT_COLORS[style]||"#B5703F", display:"inline-block" }}/>
                </div>
                <div style={{ fontWeight:500, fontSize:13, marginBottom:3, color:"#2E2A24" }}>{style}</div>
                <div style={{ fontSize:11, color:"#6b6b7a" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FOR SCHOOLS BAND */}
        <div className="for-schools" style={{ display:"grid", gridTemplateColumns:"1.1fr 0.9fr", gap:36, alignItems:"center", background:"#7C8B6F", borderRadius:16, padding:"44px 40px", marginBottom:52, overflow:"hidden" }}>
          <div>
            <p style={{ color:"rgba(255,255,255,0.75)", fontSize:11, letterSpacing:3, textTransform:"uppercase", marginBottom:12 }}>For Yoga Schools</p>
            <h2 className="serif" style={{ color:"white", fontSize:"clamp(26px,3.5vw,36px)", fontWeight:300, lineHeight:1.15, marginBottom:14 }}>Run a yoga school?<br/>Fill more spots.</h2>
            <p style={{ color:"rgba(255,255,255,0.85)", fontSize:15, lineHeight:1.7, marginBottom:22, maxWidth:440 }}>
              {SCHOOL_PITCH}
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:26 }}>
              {[
                SCHOOL_PROMISE,
                "Enquiries arrive by email with the student's dates and details",
                "Get a Verified badge after our manual review",
              ].map(b => (
                <div key={b} style={{ display:"flex", alignItems:"center", gap:10, color:"white", fontSize:14 }}>
                  <CheckCircle size={18} color="rgba(255,255,255,0.95)" style={{ flexShrink:0 }} />
                  <span>{b}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              <button onClick={() => onSwitch("school-signup")} style={{ padding:"14px 28px", background:"#FAF7F1", border:"none", color:"#5F6E54", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:"uppercase", borderRadius:8, fontWeight:700, minHeight:48 }}>List your school →</button>
              <button onClick={() => onSwitch("login")} style={{ padding:"14px 24px", background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.45)", color:"white", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:"uppercase", borderRadius:8, minHeight:48 }}>School login</button>
            </div>
          </div>
          <div className="for-schools-stats" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {[
              { big:"Free", small:"to list and to receive enquiries" },
              { big:"0%", small:"commission" },
              { big:"Direct", small:"students pay your school" },
              { big:"Yours", small:"payment & cancellation terms" },
            ].map(s => (
              <div key={s.small} style={{ background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:12, padding:"22px 18px", textAlign:"center" }}>
                <div className="serif" style={{ color:"white", fontSize:34, fontWeight:400, lineHeight:1, marginBottom:6 }}>{s.big}</div>
                <div style={{ color:"rgba(255,255,255,0.8)", fontSize:12, lineHeight:1.4 }}>{s.small}</div>
              </div>
            ))}
          </div>
        </div>

        {/* QUOTE CAROUSEL */}
        <QuoteCarousel />

        {/* FAQ — replaces testimonials until we have real reviews */}
        <div className="section-mb" style={{ marginBottom:52 }}>
          <h2 className="serif section-title" style={{ fontSize:30, color:"#2E2A24", fontWeight:300, marginBottom:4, textAlign:"center" }}>Frequently Asked Questions</h2>
          <p style={{ color:"#6b6b7a", fontSize:14, marginBottom:28, textAlign:"center" }}>Everything you need to know — for students and schools</p>
          <div style={{ maxWidth:760, margin:"0 auto", display:"flex", flexDirection:"column", gap:12 }}>
            {HOME_FAQ.map((f,i) => (
              <details key={i} className="faq-item" style={{ background:"white", border:"1px solid rgba(181,112,63,0.2)", borderRadius:10, padding:"4px 20px" }}>
                <summary style={{ listStyle:"none", cursor:"pointer", padding:"16px 0", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, fontSize:15, fontWeight:600, color:"#2E2A24" }}>
                  {f.q}
                  <span className="faq-plus" style={{ color:"#B5703F", fontSize:22, fontWeight:300, lineHeight:1, flexShrink:0 }}>+</span>
                </summary>
                <p style={{ fontSize:14, color:"#4a4a5a", lineHeight:1.8, paddingBottom:16, marginTop:-4 }}>{f.a}</p>
              </details>
            ))}
          </div>
          <p style={{ textAlign:"center", fontSize:13, color:"#6b6b7a", marginTop:22 }}>
            Running a school? <a href="/help/schools" style={{ color:"#B5703F", fontWeight:600, textDecoration:"none" }}>Read the FAQ for schools →</a>
            <span style={{ margin:"0 8px", color:"#c8bfb2" }}>·</span>
            Still have questions? <a href="mailto:support@yogastay.app" style={{ color:"#B5703F", fontWeight:600, textDecoration:"none" }}>Email our team →</a>
          </p>
        </div>

        {/* CTA */}
        <div className="cta-section" style={{ background:"linear-gradient(135deg,#985A30,#B5703F)", borderRadius:12, padding:"48px 40px", textAlign:"center", marginBottom:52 }}>
          <h2 className="serif" style={{ color:"white", fontSize:"clamp(24px,4vw,34px)", fontWeight:300, marginBottom:10 }}>Ready to Start Your Journey?</h2>
          <p style={{ color:"rgba(255,255,255,0.75)", fontSize:15, marginBottom:28, maxWidth:400, margin:"0 auto 28px" }}>Find a verified school for your next retreat.</p>
          <div className="cta-buttons" style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button onClick={() => onBrowse("", "retreats")} style={{ padding:"14px 28px", background:"#FFF1D5", border:"none", color:"#985A30", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:2, textTransform:"uppercase", borderRadius:6, fontWeight:600, minHeight:48 }}>Find a Retreat →</button>
            <button onClick={() => onSwitch("school-signup")} style={{ padding:"14px 28px", background:"rgba(255,255,255,0.2)", border:"1px solid rgba(255,255,255,0.4)", color:"white", cursor:"pointer", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:2, textTransform:"uppercase", borderRadius:6, minHeight:48 }}>List Your School</button>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ background:"#2E2A24", padding:"40px 24px 28px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div className="footer-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:32, marginBottom:32 }}>
            <div>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                <YogaIcon size={18} color="rgba(255,255,255,0.7)"/>
                <span className="serif" style={{ color:"white", fontSize:18, fontWeight:300 }}>YogaStay</span>
              </div>
              <p style={{ color:"rgba(255,255,255,0.4)", fontSize:13, lineHeight:1.8 }}>Find verified yoga schools and retreats worldwide. Free enquiries — you pay the school directly.</p>
            </div>
            <div>
              <h4 style={{ color:"rgba(255,255,255,0.7)", fontSize:11, letterSpacing:0.8, textTransform:"uppercase", marginBottom:14 }}>Explore</h4>
              {[
                ["Yoga Retreats",     () => onBrowse("", "retreats")],
                ["Meditation",        () => onBrowse("", "browse", "Meditation")],
                ["Weekend Retreats",  () => onBrowse("", "retreats", "All", "Weekend")],
                ["Yoga Teacher Training",  () => onBrowse("", "teacher-training")],
                ...(INDEPENDENT_TEACHERS ? [["Online Classes",    () => onBrowse("", "teachers")]] : []),
              ].map(([link, go]) => (
                <div key={link} style={{ marginBottom:8 }}><button onClick={go} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:13, padding:0, textAlign:"left", minHeight:36 }}>{link}</button></div>
              ))}
              {[["Blog","/blog/"],["All schools","/schools/"]].map(([label,href]) => (
                <div key={label} style={{ marginBottom:8 }}><a href={href} style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:13, textDecoration:"none", display:"inline-flex", minHeight:36, alignItems:"center" }}>{label}</a></div>
              ))}
            </div>
            <div>
              <h4 style={{ color:"rgba(255,255,255,0.7)", fontSize:11, letterSpacing:0.8, textTransform:"uppercase", marginBottom:14 }}>Destinations</h4>
              {[["Rishikesh, India","/yoga/rishikesh/"],["Bali, Indonesia","/yoga/bali/"],["Kerala, India","/yoga/kerala/"],["Goa, India","/yoga/goa/"],["Mysore, India","/yoga/mysore/"]].map(([dest, href]) => (
                <div key={dest} style={{ marginBottom:8 }}><a href={href} style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:13, textDecoration:"none", display:"inline-flex", minHeight:36, alignItems:"center" }}>{dest}</a></div>
              ))}
            </div>
            <div>
              <h4 style={{ color:"rgba(255,255,255,0.7)", fontSize:11, letterSpacing:0.8, textTransform:"uppercase", marginBottom:14 }}>For Schools</h4>
              {[["List Your School", () => onSwitch("school-signup")],["School Login", () => onSwitch("login")]].map(([link, go]) => (
                <div key={link} style={{ marginBottom:8 }}><button onClick={go} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:13, padding:0, textAlign:"left", minHeight:36 }}>{link}</button></div>
              ))}
              <div style={{ marginBottom:8 }}><a href="/help/schools" style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:13, textDecoration:"none", display:"inline-flex", minHeight:36, alignItems:"center" }}>How It Works for Schools</a></div>
            </div>
            <div>
              <h4 style={{ color:"rgba(255,255,255,0.7)", fontSize:11, letterSpacing:0.8, textTransform:"uppercase", marginBottom:14 }}>Support</h4>
              <div style={{ marginBottom:14 }}>
                <a href="mailto:support@yogastay.app" style={{ color:"#B5703F", fontSize:14, fontWeight:500, textDecoration:"none", display:"flex", alignItems:"center", gap:6 }}>
                  <Mail size={14}/> support@yogastay.app
                </a>
                <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11, marginTop:5, lineHeight:1.6 }}>We reply within 24 hours</p>
              </div>
              {[[<BookOpen size={13}/>, "School issues"],[<User size={13}/>, "Student issues"],[<AlertTriangle size={13}/>, "Report a listing"]].map(([ic,title])=>(
                <div key={title} style={{ marginBottom:10 }}>
                  <a href={`mailto:support@yogastay.app?subject=${encodeURIComponent(title)}`} style={{ color:"rgba(255,255,255,0.45)", fontSize:12, textDecoration:"none", display:"flex", gap:6, alignItems:"center", lineHeight:1.5 }}>
                    <span style={{ flexShrink:0 }}>{ic}</span><span>{title}</span>
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="footer-newsletter" style={{ background:"rgba(255,255,255,0.05)", borderRadius:8, padding:"20px 24px", marginBottom:24, display:"flex", gap:16, alignItems:"center", flexWrap:"wrap" }}>
            <div style={{ flex:1, minWidth:180 }}>
              <h4 style={{ color:"white", fontSize:14, fontWeight:400, marginBottom:4 }}>Get retreat inspiration in your inbox</h4>
              <p style={{ color:"rgba(255,255,255,0.4)", fontSize:13 }}>New schools, retreats and guides. No spam.</p>
            </div>
            {nlState === "success" ? (
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 18px", background:"rgba(39,174,96,0.15)", border:"1px solid rgba(39,174,96,0.35)", borderRadius:6 }}>
                <CheckCircle size={18} color="#7DCEA0"/>
                <div>
                  <div style={{ color:"#7DCEA0", fontSize:13, fontWeight:600 }}>You're subscribed!</div>
                  <div style={{ color:"rgba(255,255,255,0.45)", fontSize:11, marginTop:1 }}>We'll write when there's something worth reading.</div>
                </div>
              </div>
            ) : nlState === "duplicate" ? (
              <div style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 18px", background:"rgba(181,112,63,0.1)", border:"1px solid rgba(181,112,63,0.3)", borderRadius:6 }}>
                <CheckCircle size={16} color="rgba(181,112,63,0.8)"/>
                <div style={{ color:"rgba(255,255,255,0.6)", fontSize:13 }}>You're already subscribed — we've got you!</div>
              </div>
            ) : (
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                <div style={{ position:"relative" }}>
                  <input
                    type="email"
                    value={nlEmail}
                    onChange={e => { setNlEmail(e.target.value); if (nlState === "error") setNlState("idle"); }}
                    onKeyDown={e => e.key === "Enter" && handleSubscribe()}
                    placeholder="your@email.com"
                    style={{ padding:"11px 16px", border:`1px solid ${nlState==="error"?"rgba(192,57,43,0.6)":"rgba(255,255,255,0.15)"}`, borderRadius:4, background:"rgba(255,255,255,0.08)", color:"white", fontFamily:"'Jost',sans-serif", fontSize:14, width:220, minHeight:44, outline:"none" }}
                  />
                  {nlState === "error" && (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, fontSize:11, color:"#E07B7B", whiteSpace:"nowrap" }}>
                      Please enter a valid email address
                    </div>
                  )}
                </div>
                <button
                  onClick={handleSubscribe}
                  disabled={nlState === "loading"}
                  style={{ padding:"11px 20px", background:"#B5703F", border:"none", borderRadius:4, cursor: nlState==="loading"?"wait":"pointer", color:"white", fontFamily:"'Jost',sans-serif", fontSize:12, letterSpacing:1.5, textTransform:"uppercase", whiteSpace:"nowrap", minHeight:44, opacity: nlState==="loading"?0.7:1 }}>
                  {nlState === "loading" ? "..." : "Subscribe"}
                </button>
              </div>
            )}
          </div>

          <div className="footer-bottom" style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:20, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
            <span style={{ color:"rgba(255,255,255,0.3)", fontSize:12 }}>© 2026 YogaStay · All rights reserved</span>
            <div style={{ display:"flex", gap:16, flexWrap:"wrap", alignItems:"center" }}>
              <a href="/privacy" style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", minHeight:36 }}>Privacy Policy</a>
              <a href="/terms" style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", minHeight:36 }}>Terms of Service</a>
              <a href="mailto:support@yogastay.app" style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:5, minHeight:36 }}>
                Contact Us
              </a>
              <a href="https://www.instagram.com/yogastaysocial/" target="_blank" rel="noopener noreferrer" aria-label="YogaStay on Instagram"
                style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:6, minHeight:36 }}
                onMouseEnter={e=>e.currentTarget.style.color="#E1306C"} onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.45)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
                @yogastaysocial
              </a>
              <a href="https://www.facebook.com/profile.php?id=61572021518691" target="_blank" rel="noopener noreferrer" aria-label="YogaStay on Facebook"
                style={{ color:"rgba(255,255,255,0.45)", fontFamily:"'Jost',sans-serif", fontSize:12, textDecoration:"none", display:"flex", alignItems:"center", gap:6, minHeight:36 }}
                onMouseEnter={e=>e.currentTarget.style.color="#1877F2"} onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.45)"}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
                Facebook
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CookieBanner() {
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem("ys_cookie_consent"); } catch { return true; }
  });
  if (!visible) return null;
  // The choice now reaches Google Analytics. Consent defaults to denied in
  // public/index.html (and on the static pages); only Accept grants it. Before
  // this, Decline was stored and then ignored — analytics loaded regardless.
  const choose = (choice) => {
    try { localStorage.setItem("ys_cookie_consent", choice); } catch {}
    try { window.gtag?.("consent", "update", { analytics_storage: choice === "accepted" ? "granted" : "denied" }); } catch {}
    setVisible(false);
  };
  const accept = () => choose("accepted");
  const decline = () => choose("declined");
  return (
    <div style={{
      position:"fixed", bottom:0, left:0, right:0, zIndex:9999,
      background:"#2C2416", color:"rgba(255,255,255,0.9)",
      fontFamily:"'DM Sans','Jost',sans-serif", fontSize:13,
      padding:"16px 24px", display:"flex", alignItems:"center",
      justifyContent:"space-between", gap:16, flexWrap:"wrap",
      borderTop:"2px solid rgba(180,148,100,0.3)",
      boxShadow:"0 -4px 24px rgba(0,0,0,0.25)"
    }}>
      <p style={{ margin:0, flex:1, minWidth:220, lineHeight:1.6, color:"rgba(255,255,255,0.8)" }}>
        We use essential browser storage to keep you signed in. With your permission we also use Google Analytics
        cookies to understand how the site is used. Analytics stays off unless you click{" "}
        <strong style={{ color:"#E8C87A" }}>Accept</strong>.{" "}
        <a href="/privacy" style={{ color:"#C49A4A", textDecoration:"underline" }}>Privacy Policy</a>
      </p>
      <div style={{ display:"flex", gap:10, flexShrink:0 }}>
        <button onClick={decline} style={{
          padding:"8px 18px", background:"transparent", border:"1px solid rgba(180,148,100,0.4)",
          borderRadius:6, cursor:"pointer", color:"rgba(255,255,255,0.6)",
          fontFamily:"'DM Sans','Jost',sans-serif", fontSize:12, letterSpacing:0.5, minHeight:36
        }}>Decline</button>
        <button onClick={accept} style={{
          padding:"8px 22px", background:"#B47A2A", border:"none",
          borderRadius:6, cursor:"pointer", color:"white",
          fontFamily:"'DM Sans','Jost',sans-serif", fontSize:12, fontWeight:600,
          letterSpacing:0.5, minHeight:36
        }}>Accept All</button>
      </div>
    </div>
  );
}

export default function App() {
  // Deep-link support: static SEO pages (e.g. /school/<slug>/) send visitors to
  // /browse?school=<id>. Honour the path/param on first load so they land on the
  // browse screen (and open that school) instead of the marketing home page.
  const [browseSchoolId, setBrowseSchoolId] = useState(() => { try { return new URLSearchParams(window.location.search).get("school") || null; } catch { return null; } });
  // A bare single-segment path that is not a real route is a candidate vanity
  // URL (yogastay.app/<slug>). Captured here so first paint can go straight to
  // browse instead of flashing the marketing page before the lookup returns.
  const [pendingSlug] = useState(() => { try { return slugFromPath(); } catch { return null; } });
  // /<school-slug>/<retreat-slug> — a shared retreat link.
  const [pendingNested] = useState(() => { try { return nestedSlugFromPath(); } catch { return null; } });
  const [browseRetreatId, setBrowseRetreatId] = useState(null);
  const [screen, setScreen] = useState(() => {
    try {
      const p = window.location.pathname || "";
      // /admin, /dashboard … need a signed-in user (A10). Logged out, they
      // used to show the home page with the URL still saying /admin.
      const signedIn = !!(localStorage.getItem("yogastay_user") || sessionStorage.getItem("yogastay_user"));
      // Already signed in and sent to /signin?next=…: go straight there.
      const nx = safeNext(new URLSearchParams(window.location.search).get("next"));
      if (signedIn && p.startsWith("/signin") && nx) window.history.replaceState(window.history.state, "", nx);
      if (isProtectedPath(p) && !signedIn) {
        const next = p + window.location.search;
        window.history.replaceState(window.history.state, "", `/signin?next=${encodeURIComponent(next)}`);
        return "login";
      }
      if (p.startsWith("/browse") || new URLSearchParams(window.location.search).get("school")) return "browse";
      if (p === "/teacher-training" || p === "/teacher-training/") return "browse";
      // /teach is the deep link used by the blog guides' "list yourself as a
      // teacher" calls to action. Must be tested before slugFromPath(), or
      // "teach" reads as a vanity slug and gets looked up as a profile.
      if (p === "/teach" || p.startsWith("/teach/")) return INDEPENDENT_TEACHERS ? "teacher-signup" : "landing";
      // /signin is the deep link emails use. Without it a "sign in and finish
      // your listing" email drops the reader on the landing page to hunt for
      // the button, which is most of the reason such an email gets ignored.
      if (p === "/signin" || p.startsWith("/signin/")) return "login";
      if (slugFromPath() || nestedSlugFromPath()) return "browse";
    } catch {}
    return "landing";
  });
  // /claim/<token> — a school opening the private preview of a profile we
  // drafted for it. Read once: it is a landing page, not somewhere you navigate to.
  const [claimToken] = useState(() => { try { const m = (window.location.pathname || "").match(/^\/claim\/([A-Za-z0-9_-]{32,})\/?$/); return m ? m[1] : null; } catch { return null; } });
  const [user, setUser] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStyle, setFilterStyle] = useState("All Styles");
  const [browseSearch,       setBrowseSearch]       = useState("");
  const [browseTab,          setBrowseTab]          = useState(() => { try { return /^\/teacher-training\/?$/.test(window.location.pathname) ? "teacher-training" : "browse"; } catch { return "browse"; } });
  const [browseTtPreset,     setBrowseTtPreset]     = useState(null);   // from a homepage "Become a yoga teacher" quiz
  const [browseFilterStyle,  setBrowseFilterStyle]  = useState("All");
  const [browseFilterDuration, setBrowseFilterDuration] = useState("Any Duration");
  const [selectedMonth, setSelectedMonth] = useState("");

  const [showDashboard, setShowDashboard] = useState(true);
  const AUTH_SCREENS = ["login", "school-signup", "student-signup", "teacher-signup"];
  const prevScreenRef = useRef("landing");

  // Tag the initial history entry
  useEffect(() => {
    window.history.replaceState({ yogastay: "landing" }, "");
  }, []);

  // Resolve a vanity URL to the listing it points at. An address matching
  // nothing falls back to the home page rather than showing an error — a stale
  // or mistyped link should feel like a wrong turn, not a broken site.
  useEffect(() => {
    if (!pendingNested) return;
    let on = true;
    (async () => {
      const hit = await resolveNestedSlug(pendingNested);
      if (!on) return;
      if (hit?.schoolId) {
        setBrowseSchoolId(hit.schoolId);
        if (hit.retreatId) setBrowseRetreatId(hit.retreatId);
        setScreen("browse");
      } else {
        setScreen("landing");
        window.history.replaceState({ yogastay: "landing" }, "", "/");
      }
    })();
    return () => { on = false; };
  }, [pendingNested]);

  useEffect(() => {
    if (!pendingSlug) return;
    let on = true;
    (async () => {
      const hit = await resolveSlug(pendingSlug);
      if (!on) return;
      if (hit?.kind === "school") {
        setBrowseSchoolId(hit.id);
        setScreen("browse");
      } else if (hit?.kind === "teacher") {
        setBrowseTab("teachers");
        setBrowseSearch(hit.name || "");
        setScreen("browse");
      } else {
        setScreen("landing");
        window.history.replaceState({ yogastay: "landing" }, "", "/");
      }
    })();
    return () => { on = false; };
  }, [pendingSlug]);

  // Push history entries on screen changes
  useEffect(() => {
    if (AUTH_SCREENS.includes(screen) && !AUTH_SCREENS.includes(prevScreenRef.current)) {
      window.history.pushState({ yogastay: "auth" }, "");
    }
    if (screen === "browse" && prevScreenRef.current !== "browse") {
      window.history.pushState({ yogastay: "browse" }, "");
    }
    prevScreenRef.current = screen;
  }, [screen]);

  // Handle browser back button
  useEffect(() => {
    const handlePop = () => {
      if (user && showDashboard) {
        // Dashboard is active — dispatch event so the dashboard handles its own tab navigation
        window.dispatchEvent(new CustomEvent("yogastay:back"));
        // Re-push a history entry so the next back press is also intercepted
        window.history.pushState({ yogastay: "dashboard" }, "");
      } else {
        setShowDashboard(false);
        setScreen("landing");
      }
    };
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, [user, showDashboard]);

  useEffect(() => {
    // ── Keeping the session alive ─────────────────────────────────────────────
    // Access tokens last an hour. This used to be renewed only by a 50-minute
    // setInterval, which a phone pauses whenever the browser is in the
    // background — and a reopened tab restored the stored token as-is. So a
    // school that signed in once and came back later worked on an expired
    // token: every save was rejected and nothing it changed stuck. Now the
    // token is renewed on start, whenever the app comes back to the
    // foreground, and on a timer, if it expires within the next five minutes.
    const where = () => localStorage.getItem("yogastay_user") ? localStorage : sessionStorage;
    let inflight = null;
    // force: renew even if the token looks valid (the server just said 401).
    // keepSession: on a refused refresh token, report null instead of signing
    // out — a dashboard mid-save keeps its form and asks the user to sign in.
    const refreshSession = ({ force = false, keepSession = false } = {}) => {
      const st = where();
      const saved = st.getItem("yogastay_user");
      if (!saved) return Promise.resolve(null);
      let userData; try { userData = JSON.parse(saved); } catch { return Promise.resolve(null); }
      if (!force && tokenExpiry(userData.token) - Date.now() > 5 * 60 * 1000) return Promise.resolve(userData);
      const refreshToken = st.getItem("yogastay_refresh_token") || localStorage.getItem("yogastay_refresh_token");
      if (!refreshToken) return Promise.resolve(userData);
      if (inflight) return inflight;            // one exchange at a time — a refresh token is single-use
      inflight = (async () => {
        try {
          const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
            method: "POST",
            headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });
          const data = await res.json().catch(() => ({}));
          if (data.access_token) {
            const updated = { ...JSON.parse(st.getItem("yogastay_user") || saved), token: data.access_token };
            st.setItem("yogastay_user", JSON.stringify(updated));
            if (data.refresh_token) st.setItem("yogastay_refresh_token", data.refresh_token);
            setUser(prev => prev ? { ...prev, token: data.access_token } : prev);
            return updated;
          }
          // The auth server refused the refresh token (signed out elsewhere,
          // revoked, too old). Carrying on with a dead token only produces
          // saves that silently fail, so sign out and ask for a fresh login.
          if (res.status === 400 || res.status === 401 || res.status === 403) {
            if (keepSession) return null;
            ["yogastay_user", "yogastay_token", "yogastay_refresh_token"].forEach(k => { localStorage.removeItem(k); sessionStorage.removeItem(k); });
            setUser(null);
            setScreen("login");
            return null;
          }
          return userData;                      // network trouble: keep going, try again later
        } catch { return userData; }
        finally { inflight = null; }
      })();
      return inflight;
    };

    const saved = localStorage.getItem("yogastay_user") || sessionStorage.getItem("yogastay_user");
    if (saved) {
      (async () => {
        try {
          // Renew first if needed, so the dashboard's first load does not go
          // out on a token that has already expired.
          const u = await refreshSession();
          if (!u) return;
          // Back-fill multi-role fields for sessions saved before this existed
          if (!u.activeRole && u.role) u.activeRole = u.role;
          if (!u.roles && u.role) u.roles = [u.role];
          setUser(u);
          window.history.pushState({ yogastay: "dashboard" }, "");
          // Re-detect roles in the background so a newly-added role shows up
          if (u.userId && u.token) {
            detectRoles(u.userId, u.token, u.role).then(roles => {
              if (roles.length > 1) setUser(prev => prev ? { ...prev, roles } : prev);
            });
          }
        } catch {}
      })();
    }

    const onForeground = () => { if (document.visibilityState === "visible") refreshSession(); };
    document.addEventListener("visibilitychange", onForeground);
    window.addEventListener("focus", onForeground);
    const refreshInterval = setInterval(() => refreshSession(), 4 * 60 * 1000);
    // Dashboards call this when a write comes back 401, renew, and retry once.
    window.__ysRefreshSession = refreshSession;

    const handleOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("access_token")) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        if (token) {
          localStorage.setItem("yogastay_token", token);
          if (refreshToken) localStorage.setItem("yogastay_refresh_token", refreshToken);

          const userRes = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
            headers: { apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` }
          });
          const userData = await userRes.json();
          const userId = userData?.id;
          const email = userData?.email;
          const name = userData?.user_metadata?.full_name || userData?.user_metadata?.name || email;

          const profileRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?id=eq.${userId}`, {
            headers: { apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` }
          });
          const profiles = await profileRes.json();

          if (!Array.isArray(profiles) || profiles.length === 0) {
            // Signup intent — this browser, or the account itself if they
            // confirmed on a different device.
            const intent = readSignupIntent(userData?.user_metadata, email);
            if (intent) {
              try {
                clearSignupIntent();
                // Create profile + role row now that we have a valid token
                const ipRes = await fetch(`${SUPABASE_URL}/rest/v1/profiles?on_conflict=id`, {
                  method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"resolution=merge-duplicates,return=representation" },
                  body:JSON.stringify({ id:userId, role:intent.role, full_name:intent.name||name }),
                });
                if (!ipRes.ok) { const e = await ipRes.text(); console.error("profiles insert failed:", e); throw new Error(e); }
                if (intent.role === "teacher") {
                  const tCheck3 = await fetch(`${SUPABASE_URL}/rest/v1/teachers?user_id=eq.${userId}&select=id&limit=1`, { headers:{ apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` } });
                  const tExist3 = await tCheck3.json();
                  if (!Array.isArray(tExist3) || tExist3.length === 0) {
                    await fetch(`${SUPABASE_URL}/rest/v1/teachers`, {
                      method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"return=representation" },
                      body:JSON.stringify({ user_id:userId, name:intent.name||name, email:intent.email||email, is_independent:true, verification_status:"unverified", subscription_status:"inactive", specialties:[], teaching_levels:[], languages:[], certificate_urls:[], document_urls:[], online_classes:[] }),
                    });
                  }
                } else if (intent.role === "student") {
                  await fetch(`${SUPABASE_URL}/rest/v1/students?on_conflict=user_id`, {
                    method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"resolution=merge-duplicates,return=representation" },
                    body:JSON.stringify({ user_id:userId, full_name:intent.name||name, email:intent.email||email, yoga_goals:intent.goals||[] }),
                  });
                } else if (intent.role === "school") {
                  // This branch did not exist. A school that confirmed its email
                  // got a profiles row and no schools row, so it arrived with
                  // nothing listed and had to re-enter all of step 2.
                  const sCheck = await fetch(`${SUPABASE_URL}/rest/v1/schools?user_id=eq.${userId}&select=id&limit=1`, { headers:{ apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}` } });
                  const sExist = await sCheck.json();
                  if (!Array.isArray(sExist) || sExist.length === 0) {
                    const sRes = await fetch(`${SUPABASE_URL}/rest/v1/schools`, {
                      method:"POST", headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${token}`, Prefer:"return=representation" },
                      body:JSON.stringify({ user_id:userId, name:intent.name||name, description:intent.description||null, location:intent.location||null, address:intent.address||null, style:intent.style||null, contact_email:intent.email||email||null, verified:false }),
                    });
                    if (!sRes.ok) { console.error("schools insert failed:", await sRes.text()); }
                  }
                }
                await sendRoleWelcome({ role:intent.role, email:intent.email||email, name:intent.name||name, schoolName:intent.name||name, schoolCity:intent.location, token });
                const userObj = { token, userId, role:intent.role, name:intent.name||name, email:intent.email||email };
                setGoogleUser(null);
                setUser(userObj);
                localStorage.setItem("yogastay_user", JSON.stringify(userObj));
                if (refreshToken) localStorage.setItem("yogastay_refresh_token", refreshToken);
              } catch(e) {
                setGoogleUser({ token, userId, email, name });
                setScreen("google-role-select");
              }
            } else {
              // New Google user — pick a role
              setGoogleUser({ token, userId, email, name });
              setScreen("google-role-select");
            }
          } else {
            // Existing user — clear googleUser so role-select never shows
            const profile = profiles[0];
            const userObj = { token, userId, role:profile.role, name:profile.full_name||name, email };
            setGoogleUser(null);
            setUser(userObj);
            localStorage.setItem("yogastay_user", JSON.stringify(userObj));
            if (refreshToken) localStorage.setItem("yogastay_refresh_token", refreshToken);
          }
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };
    handleOAuthCallback();

    return () => {
      clearInterval(refreshInterval); delete window.__ysRefreshSession;
      document.removeEventListener("visibilitychange", onForeground);
      window.removeEventListener("focus", onForeground);
    };
  }, []);

  const persistUser = (u) => {
    const store = u.rememberMe === false ? sessionStorage : localStorage;
    store.setItem("yogastay_user", JSON.stringify(u));
  };

  const handleSuccess = (userData) => {
    const enriched = { ...userData, roles: userData.roles || (userData.role ? [userData.role] : []), activeRole: userData.activeRole || userData.role };
    setUser(enriched);
    setShowDashboard(true);
    setScreen("landing"); // always land here so back-button can find landing state
    // Remember me: use localStorage (persist) vs sessionStorage (session only)
    persistUser(enriched);
    if (userData.refresh_token) (userData.rememberMe === false ? sessionStorage : localStorage).setItem("yogastay_refresh_token", userData.refresh_token);
    window.history.pushState({ yogastay: "dashboard" }, "");
    // Detect any additional roles this login holds (async, non-blocking)
    if (userData.userId && userData.token) {
      detectRoles(userData.userId, userData.token, userData.role).then(roles => {
        if (roles.length > 1) {
          setUser(prev => { const next = { ...prev, roles }; persistUser(next); return next; });
        }
      });
    }
  };

  const handleSwitchRole = (role) => {
    setUser(prev => {
      if (!prev || !prev.roles?.includes(role)) return prev;
      const next = { ...prev, activeRole: role };
      persistUser(next);
      return next;
    });
    setShowDashboard(true);
    setScreen("landing");
  };

  const handleLogout = async () => {
    if (user?.token) {
      await fetch(`${SUPABASE_URL}/auth/v1/logout`, {
        method:"POST",
        headers:{ "Content-Type":"application/json", apikey:SUPABASE_ANON_KEY, Authorization:`Bearer ${user.token}` },
      });
    }
    localStorage.removeItem("yogastay_user");
    localStorage.removeItem("yogastay_token");
    localStorage.removeItem("yogastay_refresh_token");
    sessionStorage.removeItem("yogastay_user");
    sessionStorage.removeItem("yogastay_refresh_token");
    setUser(null);
    setShowDashboard(true);
    setScreen("landing");
    // Leave /admin/… or /dashboard/… behind with the session.
    if (isProtectedPath(window.location.pathname)) window.history.replaceState(window.history.state, "", "/");
  };

  const handleBook = (cls, school) => console.log("Book:", cls.title);
  const mode = screen === "school-signup" ? "school" : screen === "teacher-signup" ? "teacher" : "student";

  // Shown while a split screen chunk loads. Full-viewport and in the app's own
// background so the swap to real content shifts nothing.
function ScreenFallback() {
  return <div style={{ minHeight:"100vh", background:"#FFF1D5" }} aria-busy="true" />;
}

// Logged-in users → their dashboard (unless back-button pressed, then fall through to landing)
  // Before the signed-in branch on purpose: the link must show the draft even to
  // someone who happens to be logged in as something else.
  if (claimToken) {
    return (
      <>
        <GlobalStyles />
        <Suspense fallback={<ScreenFallback />}><ClaimProfile token={claimToken} /></Suspense>
      </>
    );
  }

  if (user && showDashboard) {
    return (
      <>
        <GlobalStyles />
        <CookieBanner />
        <Suspense fallback={<ScreenFallback />}>
        {(() => { const view = user.activeRole || user.role; return (
          view === "admin"
          ? <AdminDashboard user={user} onLogout={handleLogout} />
          : view === "school"
          ? <SchoolDashboard user={user} onLogout={handleLogout} />
          : view === "teacher"
          ? <TeacherDashboard user={user} onLogout={handleLogout} />
          : <StudentBrowse user={user} onLogout={handleLogout} onBook={handleBook} initialSchoolId={browseSchoolId} initialRetreatId={browseRetreatId}
              initialTab={/^\/teacher-training\/?$/.test(window.location.pathname) ? "teacher-training" : undefined} />
        ); })()}
        </Suspense>
        <AccountSwitcher roles={user.roles} activeRole={user.activeRole || user.role} onSwitchRole={handleSwitchRole} />
      </>
    );
  }

  // Anonymous browse — no account needed, login gate fires at booking
  if (screen === "browse") {
    return (
      <>
        <GlobalStyles />
        <CookieBanner />
        <Suspense fallback={<ScreenFallback />}>
        <StudentBrowse
          user={null}
          onLogout={() => setScreen("landing")}
          onBook={handleBook}
          onLoginRequired={(s) => setScreen(s || "login")}
          initialSearch={browseSearch}
          initialTab={browseTab}
          initialTtPreset={browseTtPreset}
          initialFilterStyle={browseFilterStyle}
          initialFilterDuration={browseFilterDuration}
          initialSchoolId={browseSchoolId}
          initialRetreatId={browseRetreatId}
        />
        </Suspense>
      </>
    );
  }

  // Auth screens + landing
  return (
    <>
      <GlobalStyles />
      <CookieBanner />
      {screen === "google-role-select" && googleUser && !user ? (
        <GoogleRoleSelect googleUser={googleUser} onSuccess={handleSuccess} />
      ) : screen === "landing" ? (
        <Landing
          onSwitch={setScreen}
          onBrowse={(s, tab, style, duration, schoolId, ttPreset) => {
            if (schoolId) setBrowseSchoolId(schoolId);
            if (ttPreset) setBrowseTtPreset(ttPreset);
            setBrowseSearch(s || "");
            setBrowseTab(tab || "browse");
            setBrowseFilterStyle(style || "All");
            setBrowseFilterDuration(duration || "Any Duration");
            setScreen("browse");
          }}
          search={search}
          setSearch={setSearch}
          filterStyle={filterStyle}
          setFilterStyle={setFilterStyle}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          loggedInUser={user}
          onGoToDashboard={() => setShowDashboard(true)}
        />
      ) : (
        <div style={{ display:"flex", minHeight:"100vh" }}>
          <LeftPanel mode={mode} onLogoClick={() => setScreen("landing")} />
          <div className="auth-right-panel" style={{ flex:1, overflowY:"auto", background:"#FFF1D5" }}>
            {screen === "school-signup"  && <SchoolSignUp  onSwitch={setScreen} onSuccess={handleSuccess} />}
            {screen === "student-signup" && <StudentSignUp onSwitch={setScreen} onSuccess={handleSuccess} />}
            {screen === "teacher-signup" && (INDEPENDENT_TEACHERS ? <TeacherSignUp onSwitch={setScreen} onSuccess={handleSuccess} /> : <Login onSwitch={setScreen} onSuccess={handleSuccess} />)}
            {screen === "login" && <Login onSwitch={setScreen} onSuccess={(userData) => {
              // Back to the page that asked for sign-in (only same-site
              // /admin or /dashboard paths — never an open redirect).
              const next = safeNext(new URLSearchParams(window.location.search).get("next"));
              if (next) window.history.replaceState(window.history.state, "", next);
              handleSuccess(userData);
              // Only redirect students to browse — teachers/schools go to their dashboard via if(user)
              if (userData.role === "student" || !userData.role) setScreen("browse");
            }} />}
          </div>
        </div>
      )}
    </>
  );
}
